// theme javascript

// keep closed accordion panels out of tab order
const setCollapsiblePanelHidden = (panel, hidden) => {
  if (!panel) return;
  panel.setAttribute("aria-hidden", String(hidden));
  panel.toggleAttribute("inert", hidden);
};

const runWhenReady = (callback) => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback);
    return;
  }

  callback();
};

const whenProductAudioReady = (callback, attempt = 0) => {
  if (window.ProductAudio?.initAudioPlayers) {
    callback();
    return;
  }

  if (attempt >= 100) return;

  requestAnimationFrame(() => whenProductAudioReady(callback, attempt + 1));
};

const whenVideoWavesReady = (callback, attempt = 0) => {
  if (window.ProductAudio?.syncVideoWaves) {
    callback();
    return;
  }

  if (attempt >= 100) return;

  requestAnimationFrame(() => whenVideoWavesReady(callback, attempt + 1));
};

const MINI_CART_SECTION_ID = "mini-cart";

const setCartCount = (count) => {
  document.querySelectorAll("[data-cart-count]").forEach((el) => {
    el.textContent = String(count);
  });
};

const applyMiniCartSection = (sectionHtml) => {
  const content = document.querySelector("[data-mini-cart-content]");
  if (!content || !sectionHtml) return;

  const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
  const newContent = doc.querySelector("[data-mini-cart-content]");
  if (newContent) content.innerHTML = newContent.innerHTML;
};

const updateCartCount = async () => {
  try {
    const response = await fetch("/cart.js");
    const cart = await response.json();
    setCartCount(cart.item_count);
    return cart;
  } catch (error) {
    console.error("Error updating cart count:", error);
  }
};

// cart functionality
class Cart {
  constructor() {
    this.init();
  }

  init() {
    updateCartCount();
    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      form.addEventListener("submit", this.handleAddToCart.bind(this));
    });
  }

  async handleAddToCart(e) {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    formData.append("sections", MINI_CART_SECTION_ID);

    const addedQuantity = Number(formData.get("quantity")) || 1;
    const countEl = document.querySelector("[data-cart-count]");
    const currentCount = Number(countEl?.textContent || 0);

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) return;

      const data = await response.json();
      applyMiniCartSection(data.sections?.[MINI_CART_SECTION_ID]);
      setCartCount(currentCount + addedQuantity);
      updateCartCount();
      document.dispatchEvent(new CustomEvent("cart:open"));
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  }
}

let miniCartUpdating = false;

const changeMiniCartQuantity = async (key, quantity) => {
  if (miniCartUpdating) return;
  miniCartUpdating = true;

  try {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: key,
        quantity,
        sections: MINI_CART_SECTION_ID,
      }),
    });

    if (!response.ok) return;

    const cart = await response.json();
    setCartCount(cart.item_count);
    applyMiniCartSection(cart.sections?.[MINI_CART_SECTION_ID]);
  } catch (error) {
    console.error("Error changing cart quantity:", error);
  } finally {
    miniCartUpdating = false;
  }
};

// initialize when dom is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new Cart();
  });
} else {
  new Cart();
}

// mobile menu toggle
const initMobileMenu = () => {
  const menuToggle = document.querySelector("[data-mobile-menu-toggle]");
  const mobileMenu = document.querySelector("[data-mobile-menu]");

  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener("click", () => {
      mobileMenu.classList.toggle("hidden");
    });
  }
};

document.addEventListener("DOMContentLoaded", initMobileMenu);

// product page: variants, gallery, sold-out
class ProductForm {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector("[data-product-form]");
    const variantsEl = section.querySelector("[data-product-variants]");
    if (!this.form || !variantsEl) return;

    this.variants = JSON.parse(variantsEl.textContent);
    this.variantIdInput = this.form.querySelector("[data-product-variant-id]");
    this.priceEl = section.querySelector("[data-product-price-current]");
    this.compareEl = section.querySelector("[data-product-price-compare]");
    this.submitBtn = section.querySelector("[data-product-submit]");
    this.submitText = section.querySelector("[data-product-submit-text]");
    this.soldOutMsg = section.querySelector("[data-product-sold-out]");
    this.mainImage = section.querySelector("[data-product-gallery-main]");
    this.advanceBtn = section.querySelector("[data-product-gallery-advance]");
    this.thumbs = [...section.querySelectorAll("[data-product-gallery-thumb]")];
    this.galleryModal = section.querySelector("[data-product-gallery-modal]");
    this.galleryModalScroller = section.querySelector(
      "[data-product-gallery-modal-scroller]",
    );
    this.galleryModalCloseButtons = [
      ...section.querySelectorAll("[data-product-gallery-modal-close]"),
    ];
    this.optionInputs = this.form.querySelectorAll("[data-product-option]");
    this.preloadCache = new Map();
    this.mainImageRequestId = 0;
    this.galleryModalKeyHandler = (event) => {
      if (event.key === "Escape") this.closeGalleryModal();
    };
    this.galleryModalCloseTimeoutId = null;

    this.optionInputs.forEach((input) => {
      input.addEventListener("change", () => this.updateVariant());
    });

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        this.setMainImage(thumb);
      });
      thumb.addEventListener("pointerenter", () => this.preloadThumb(thumb), {
        passive: true,
      });
      thumb.addEventListener("focus", () => this.preloadThumb(thumb));
    });

    if (this.advanceBtn) {
      this.advanceBtn.addEventListener("click", () => {
        if (this.isMobileGallery()) {
          this.openGalleryModal();
          return;
        }

        if (this.thumbs.length > 1) this.showNextImage();
      });
    }

    this.galleryModalCloseButtons.forEach((button) => {
      button.addEventListener("click", () => this.closeGalleryModal());
    });

    window.addEventListener(
      "resize",
      () => {
        if (!this.isMobileGallery()) this.closeGalleryModal();
      },
      { passive: true },
    );

    this.updateVariant();
  }

  isMobileGallery() {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  openGalleryModal() {
    if (!this.galleryModal || !this.isMobileGallery()) return;

    if (this.galleryModalCloseTimeoutId) {
      window.clearTimeout(this.galleryModalCloseTimeoutId);
      this.galleryModalCloseTimeoutId = null;
    }

    this.galleryModal.classList.remove("hidden");
    this.galleryModal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this.galleryModalKeyHandler);

    if (this.galleryModalScroller) this.galleryModalScroller.scrollTop = 0;

    requestAnimationFrame(() => {
      if (this.galleryModalScroller) this.galleryModalScroller.scrollTop = 0;
      this.galleryModal.classList.remove("opacity-0", "pointer-events-none");
      this.galleryModal.classList.add("opacity-100");
      this.galleryModalCloseButtons[0]?.focus({ preventScroll: true });
    });
  }

  closeGalleryModal() {
    if (!this.galleryModal || this.galleryModal.classList.contains("hidden")) {
      return;
    }

    this.galleryModal.classList.remove("opacity-100");
    this.galleryModal.classList.add("opacity-0", "pointer-events-none");
    this.galleryModal.setAttribute("aria-hidden", "true");
    document.removeEventListener("keydown", this.galleryModalKeyHandler);

    this.galleryModalCloseTimeoutId = window.setTimeout(() => {
      this.galleryModal.classList.add("hidden");
      document.documentElement.classList.remove("overflow-hidden");
      document.body.classList.remove("overflow-hidden");
      this.galleryModalCloseTimeoutId = null;
    }, 300);
  }

  getSelectedOptionValues() {
    const groups = this.form.querySelectorAll("[data-product-option-group]");

    if (groups.length > 0) {
      return Array.from(groups).map((group) => {
        const input =
          group.querySelector("[data-product-option]:checked") ||
          group.querySelector("select[data-product-option]");
        return input ? input.value : null;
      });
    }

    if (this.optionInputs.length === 0) return [];

    return Array.from(this.optionInputs).map((input) => input.value);
  }

  findVariant() {
    const optionValues = this.getSelectedOptionValues();

    if (optionValues.length === 0) {
      const id = parseInt(this.variantIdInput.value, 10);
      return this.variants.find((v) => v.id === id) || this.variants[0];
    }

    if (optionValues.includes(null)) return null;

    return this.variants.find((variant) =>
      optionValues.every(
        (value, index) => variant["option" + (index + 1)] === value,
      ),
    );
  }

  formatMoney(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return "$" + (cents / 100).toFixed(2);
  }

  updateVariant() {
    const variant = this.findVariant();
    if (!variant) return;

    this.variantIdInput.value = variant.id;

    if (this.priceEl) {
      this.priceEl.textContent = this.formatMoney(variant.price);
    }

    if (this.compareEl) {
      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price
      ) {
        this.compareEl.textContent = this.formatMoney(variant.compare_at_price);
        this.compareEl.classList.remove("hidden");
      } else {
        this.compareEl.classList.add("hidden");
      }
    }

    const available = variant.available;

    if (this.priceEl) {
      this.priceEl.classList.toggle("invisible", !available);
    }

    if (this.submitBtn) {
      this.submitBtn.disabled = !available;
      this.submitBtn.classList.toggle("cursor-pointer", available);
      this.submitBtn.classList.toggle("cursor-not-allowed", !available);
    }

    if (this.submitText) {
      this.submitText.textContent = available ? "Add to Cart" : "Sold Out";
    }

    if (this.soldOutMsg) {
      this.soldOutMsg.classList.toggle("hidden", available);
    }

    if (variant.featured_image && this.mainImage) {
      const thumb = this.section.querySelector(
        `[data-product-gallery-thumb][data-image-id="${variant.featured_image.id}"]`,
      );

      if (thumb) {
        this.setMainImage(thumb);
      } else {
        this.swapMainImage({
          src: variant.featured_image.src,
          alt: variant.featured_image.alt || "",
        });
        this.highlightThumb(variant.featured_image.id);
      }
    }
  }

  getThumbImageData(thumb) {
    const img = thumb.querySelector("img");
    if (!img) return null;

    return {
      src: img.dataset.fullSrc || img.currentSrc || img.src,
      srcset: (img.dataset.srcset || "").replace(/\s+/g, " ").trim(),
      sizes: this.mainImage?.getAttribute("sizes") || "50vw",
      alt: img.alt || "",
      imageId: thumb.dataset.imageId,
    };
  }

  preloadThumb(thumb) {
    const data = this.getThumbImageData(thumb);
    if (!data?.src) return;

    this.preloadImage(data.src, data.srcset, data.sizes);
  }

  preloadImage(src, srcset = "", sizes = "50vw") {
    if (!src || this.preloadCache.has(src)) return this.preloadCache.get(src);

    const image = new Image();
    if (srcset) {
      image.sizes = sizes;
      image.srcset = srcset;
    }
    image.src = src;

    const promise = image.decode
      ? image.decode().catch(() => {})
      : Promise.resolve();

    this.preloadCache.set(src, promise);
    return promise;
  }

  async setMainImage(thumb) {
    const data = this.getThumbImageData(thumb);
    if (!data || !this.mainImage) return;

    this.highlightThumb(data.imageId);
    await this.swapMainImage(data);
  }

  showNextImage() {
    if (this.thumbs.length < 2) return;

    const currentIndex = this.thumbs.findIndex((thumb) =>
      thumb.classList.contains("is-active"),
    );
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + 1) % this.thumbs.length;

    this.setMainImage(this.thumbs[nextIndex]);
  }

  async swapMainImage({ src, srcset = "", sizes = "50vw", alt = "" }) {
    if (!this.mainImage || !src) return;
    if (this.mainImage.getAttribute("src") === src) {
      if (alt) this.mainImage.alt = alt;
      return;
    }

    const requestId = ++this.mainImageRequestId;
    await this.preloadImage(src, srcset, sizes);
    if (requestId !== this.mainImageRequestId) return;

    if (srcset) {
      this.mainImage.sizes = sizes;
      this.mainImage.srcset = srcset;
    } else {
      this.mainImage.removeAttribute("srcset");
    }

    this.mainImage.src = src;
    this.mainImage.alt = alt;
  }

  highlightThumb(imageId) {
    this.section
      .querySelectorAll("[data-product-gallery-thumb]")
      .forEach((thumb) => {
        const active = String(thumb.dataset.imageId) === String(imageId);
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-pressed", active ? "true" : "false");
      });
  }
}

const initProductForms = () => {
  document.querySelectorAll("[data-product-section]").forEach((section) => {
    new ProductForm(section);
  });
};

document.addEventListener("DOMContentLoaded", initProductForms);

const initProductSubmitBar = () => {
  const bar = document.querySelector("[data-product-submit-bar]");
  const menu = document.querySelector("[data-header-menu]");
  const footer = document.querySelector("footer");
  const cartOpen = document.querySelector("[data-cart-open]");
  const mainMenuAccordion = document.querySelector(
    "[data-main-menu-accordion]",
  );
  if (!bar || !menu) return;

  const isDesktop = () => window.matchMedia("(min-width: 768px)").matches;
  const MENU_ANIM_MS = 600;
  let footerHides = false;
  let trackRafId = 0;
  let trackUntil = 0;

  const isCartOpen = () => cartOpen?.getAttribute("aria-expanded") === "true";

  const syncPosition = () => {
    if (isDesktop()) {
      bar.style.width = "";
      bar.style.bottom = "";
      return;
    }

    const rect = menu.getBoundingClientRect();
    const gap = 10;
    bar.style.width = `${rect.width}px`;
    bar.style.bottom = `${Math.max(gap, window.innerHeight - rect.top + gap)}px`;
  };

  const syncVisibility = () => {
    if (isDesktop()) {
      bar.classList.remove("opacity-0", "pointer-events-none");
      return;
    }

    const hide = footerHides || isCartOpen();
    bar.classList.toggle("opacity-0", hide);
    bar.classList.toggle("pointer-events-none", hide);
  };

  const sync = () => {
    if (isDesktop()) {
      bar.style.width = "";
      bar.style.bottom = "";
      bar.classList.remove("opacity-0", "pointer-events-none");
      return;
    }

    syncPosition();
    syncVisibility();
  };

  const stopTracking = () => {
    if (!trackRafId) return;
    window.cancelAnimationFrame(trackRafId);
    trackRafId = 0;
  };

  const trackMenuLayout = (durationMs = MENU_ANIM_MS) => {
    stopTracking();
    trackUntil = performance.now() + durationMs;

    const tick = (now) => {
      syncPosition();
      if (now < trackUntil) {
        trackRafId = window.requestAnimationFrame(tick);
        return;
      }
      trackRafId = 0;
      syncPosition();
    };

    trackRafId = window.requestAnimationFrame(tick);
  };

  if (footer) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        footerHides = entry.isIntersecting;
        syncVisibility();
      },
      { root: null, threshold: 0 },
    );
    observer.observe(footer);
  }

  if (typeof ResizeObserver !== "undefined") {
    const resizeObserver = new ResizeObserver(() => {
      if (isDesktop()) return;
      syncPosition();
    });
    resizeObserver.observe(menu);
  }

  document.addEventListener("cart:modal:toggled", syncVisibility);
  document.addEventListener("header:main-menu:toggled", () => {
    trackMenuLayout(
      mainMenuAccordion?.hasAttribute("data-open") ? 300 : MENU_ANIM_MS,
    );
  });

  sync();
  window.addEventListener("resize", sync, { passive: true });
};

document.addEventListener("DOMContentLoaded", initProductSubmitBar);

// product page: ordered large (3) and small (2) thumbs
const GALLERY_SPAN_CLASSES = ["col-span-2", "col-span-3"];
const GALLERY_START_CLASSES = ["col-start-1", "col-start-4"];
const GALLERY_GRID_COLS = 11;

const buildGallerySpans = (count) => {
  if (count === 1) return [3];

  const spans = [3, 3];
  let remaining = count - 2;

  while (remaining > 0) {
    const smallCount = Math.min(4, remaining);
    for (let i = 0; i < smallCount; i += 1) spans.push(2);
    remaining -= smallCount;

    if (remaining > 0) {
      spans.push(3);
      remaining -= 1;
    }
  }

  return spans;
};

const initProductGalleryLayout = () => {
  document.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
    const items = [...gallery.querySelectorAll("[data-product-gallery-thumb]")];
    const spans = buildGallerySpans(items.length);
    let col = 1;

    spans.forEach((span, index) => {
      const item = items[index];
      if (!item) return;

      if (col + span - 1 > GALLERY_GRID_COLS) col = 1;

      item.classList.remove(...GALLERY_SPAN_CLASSES, ...GALLERY_START_CLASSES);
      item.classList.add(`col-span-${span}`);

      if (items.length === 1) {
        item.classList.add("col-start-4");
      } else if (col === 1) {
        item.classList.add("col-start-1");
      }

      col += span;
      if (col > GALLERY_GRID_COLS) col = 1;
    });
  });
};

document.addEventListener("DOMContentLoaded", initProductGalleryLayout);

// product page: info accordions
class ProductAccordion {
  constructor(container) {
    this.items = [...container.querySelectorAll("[data-product-accordion]")];

    this.items.forEach((item) => {
      const trigger = item.querySelector("[data-product-accordion-trigger]");
      if (!trigger) return;

      this.syncItem(item);
      trigger.addEventListener("click", () => this.toggle(item));
    });
  }

  syncItem(item) {
    const trigger = item.querySelector("[data-product-accordion-trigger]");
    const panel = item.querySelector("[data-product-accordion-panel]");
    const icon = item.querySelector("[data-product-accordion-icon]");
    const isOpen = item.hasAttribute("data-open");

    if (trigger) trigger.setAttribute("aria-expanded", isOpen);
    if (panel) setCollapsiblePanelHidden(panel, !isOpen);

    const svg = icon?.querySelector("svg");
    if (svg) {
      svg.classList.toggle("rotate-180", isOpen);
      return;
    }

    if (icon) icon.textContent = isOpen ? "▲" : "▼";
  }

  toggle(item) {
    const wasOpen = item.hasAttribute("data-open");

    this.items.forEach((other) => other.removeAttribute("data-open"));
    if (!wasOpen) item.setAttribute("data-open", "");
    this.items.forEach((other) => this.syncItem(other));
  }
}

const initProductAccordions = () => {
  document
    .querySelectorAll("[data-product-accordions]")
    .forEach((container) => {
      new ProductAccordion(container);
    });
};

document.addEventListener("DOMContentLoaded", initProductAccordions);

// shop menu mobile accordion
const initShopMenuAccordion = () => {
  const accordion = document.querySelector("[data-shop-menu-accordion]");
  if (!accordion) return;

  const trigger = accordion.querySelector("[data-shop-menu-accordion-trigger]");
  const panel = accordion.querySelector("[data-shop-menu-accordion-panel]");
  const list = accordion.querySelector("[data-shop-menu-accordion-list]");
  if (!trigger || !panel) return;

  const sync = () => {
    const isOpen = accordion.hasAttribute("data-open");
    trigger.setAttribute("aria-expanded", String(isOpen));
    setCollapsiblePanelHidden(panel, !isOpen);
  };

  // skip open animation when menu starts expanded
  if (accordion.hasAttribute("data-open") && list) {
    const prevPanelTransition = panel.style.transition;
    const prevListTransition = list.style.transition;
    panel.style.transition = "none";
    list.style.transition = "none";
    void panel.offsetHeight;
    requestAnimationFrame(() => {
      panel.style.transition = prevPanelTransition;
      list.style.transition = prevListTransition;
    });
  }

  trigger.addEventListener("click", () => {
    accordion.toggleAttribute("data-open");
    sync();
  });

  sync();
};

document.addEventListener("DOMContentLoaded", initShopMenuAccordion);

const initMainMenuAccordion = () => {
  const accordion = document.querySelector("[data-main-menu-accordion]");
  if (!accordion) return;

  const trigger = accordion.querySelector("[data-main-menu-accordion-trigger]");
  const panel = accordion.querySelector("[data-main-menu-accordion-panel]");
  const label = trigger?.querySelector("[data-main-menu-accordion-label]");
  if (!trigger || !panel || !label) return;

  const ANIM_MS = 300;
  let layoutTimeoutId = null;

  const sync = () => {
    const isOpen = accordion.hasAttribute("data-open");
    trigger.setAttribute("aria-expanded", String(isOpen));
    setCollapsiblePanelHidden(panel, !isOpen);
    label.textContent = isOpen ? "Close" : "Menu";
    document.dispatchEvent(new CustomEvent("header:main-menu:toggled"));
  };

  const scheduleLayoutSync = (delay = 0) => {
    window.clearTimeout(layoutTimeoutId);
    layoutTimeoutId = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, delay);
  };

  const closeAccordion = () => {
    if (!accordion.hasAttribute("data-open")) return;
    accordion.removeAttribute("data-open");
    sync();
    scheduleLayoutSync(ANIM_MS * 2);
  };

  trigger.addEventListener("click", (event) => {
    event.stopPropagation();
    const willOpen = !accordion.hasAttribute("data-open");

    if (willOpen) {
      document.dispatchEvent(
        new CustomEvent("header:menu:close-overlays", {
          detail: { except: "main-menu" },
        }),
      );
    }

    accordion.toggleAttribute("data-open");
    sync();
    // wait for expand+fade or fade+collapse before repositioning overlays
    scheduleLayoutSync(willOpen ? ANIM_MS : ANIM_MS * 2);
  });

  document.addEventListener("header:menu:close-overlays", (event) => {
    if (event.detail?.except === "main-menu") return;
    closeAccordion();
  });

  sync();
};

document.addEventListener("DOMContentLoaded", initMainMenuAccordion);

// featured collection: collapse cta text to first paragraph
const initFeaturedCollectionCtas = () => {
  document.querySelectorAll("[data-featured-cta]").forEach((root) => {
    if (root.dataset.featuredCtaBound === "true") return;

    const body = root.querySelector("[data-featured-cta-body]");
    const toggleWrap = root.querySelector("[data-featured-cta-toggle-wrap]");
    const toggle = root.querySelector("[data-featured-cta-toggle]");
    if (!body || !toggleWrap || !toggle) return;

    const sourceText = body.innerText.replace(/\r\n/g, "\n").trim();
    const paragraphs = sourceText
      .split(/\n\s*\n/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (paragraphs.length < 2) return;

    const formatParagraph = (text) =>
      text
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .join("<br>");

    const preview = document.createElement("div");
    preview.innerHTML = formatParagraph(paragraphs[0]);

    const panel = document.createElement("div");
    panel.className = "featured-cta-panel";
    panel.setAttribute("data-featured-cta-panel", "");
    panel.setAttribute("aria-hidden", "false");

    const panelClip = document.createElement("div");
    panelClip.className = "overflow-hidden";

    const panelInner = document.createElement("div");
    panelInner.className = "featured-cta-panel-content pt-4";
    paragraphs.slice(1).forEach((paragraph, index) => {
      const block = document.createElement("div");
      if (index > 0) block.className = "mt-4";
      block.innerHTML = formatParagraph(paragraph);
      panelInner.appendChild(block);
    });

    panelClip.appendChild(panelInner);
    panel.appendChild(panelClip);
    body.replaceChildren(preview, panel);
    toggleWrap.classList.remove("hidden");

    const sync = () => {
      const isOpen = root.hasAttribute("data-open");
      toggle.setAttribute("aria-expanded", String(isOpen));
      setCollapsiblePanelHidden(panel, !isOpen);
    };

    toggle.addEventListener("click", () => {
      root.toggleAttribute("data-open");
      sync();
    });

    sync();
    root.dataset.featuredCtaBound = "true";
  });
};

document.addEventListener("DOMContentLoaded", initFeaturedCollectionCtas);

// text & media: mobile-only details accordion for two-column blocks
const initTextMediaDetails = (scope = document) => {
  const mediaQuery = window.matchMedia("(min-width: 768px)");

  scope.querySelectorAll("[data-text-media-details]").forEach((root) => {
    if (root.dataset.textMediaDetailsBound === "true") return;

    const trigger = root.querySelector("[data-text-media-details-trigger]");
    const panel = root.querySelector("[data-text-media-details-panel]");
    if (!trigger || !panel) return;

    const sync = () => {
      const isDesktop = mediaQuery.matches;
      const isOpen = isDesktop || root.hasAttribute("data-open");

      trigger.setAttribute("aria-expanded", String(!isDesktop && isOpen));
      setCollapsiblePanelHidden(panel, !isOpen);

      if (isDesktop) {
        root.removeAttribute("data-open");
      }
    };

    trigger.addEventListener("click", () => {
      if (mediaQuery.matches) return;
      root.toggleAttribute("data-open");
      sync();
    });

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", sync);
    } else {
      mediaQuery.addListener(sync);
    }

    sync();
    root.dataset.textMediaDetailsBound = "true";
  });
};

document.addEventListener("DOMContentLoaded", () => initTextMediaDetails());

// collection mobile layout toggle
const initCollectionLayoutToggle = () => {
  const toggle = document.querySelector("[data-collection-layout-toggle]");
  const grids = [...document.querySelectorAll("[data-collection-grid]")];
  if (!toggle || grids.length === 0) return;

  const options = [...toggle.querySelectorAll("[data-layout-option]")];
  const storageKey = "collection-grid-layout";

  const setLayout = (layout) => {
    const next = layout === "single" ? "single" : "multi";
    grids.forEach((grid) => {
      grid.dataset.layout = next;
    });

    options.forEach((option) => {
      const isActive = option.dataset.layoutOption === next;
      option.setAttribute("aria-pressed", String(isActive));
      const dot = option.querySelector("[data-layout-dot]");
      if (dot) dot.classList.toggle("opacity-100", isActive);
      if (dot) dot.classList.toggle("opacity-0", !isActive);
    });

    try {
      window.sessionStorage.setItem(storageKey, next);
    } catch (error) {
      // ignore storage errors
    }
  };

  options.forEach((option) => {
    option.addEventListener("click", () => {
      setLayout(option.dataset.layoutOption);
    });
  });

  let initial = "multi";
  try {
    initial = window.sessionStorage.getItem(storageKey) || "multi";
  } catch (error) {
    initial = "multi";
  }
  setLayout(initial);
};

document.addEventListener("DOMContentLoaded", initCollectionLayoutToggle);

// shop the look: cycle featured images via inset thumb
const initLookImageCycles = () => {
  document.querySelectorAll("[data-look-image-cycle]").forEach((root) => {
    if (root.dataset.lookCycleBound === "true") return;

    const main = root.querySelector("[data-look-main]");
    const thumbBtn = root.querySelector("[data-look-thumb]");
    const thumbImg = root.querySelector("[data-look-thumb-img]");
    const dataEl = root.querySelector("[data-look-images]");
    if (!main || !thumbBtn || !thumbImg || !dataEl) return;

    let images;
    try {
      images = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(images) || images.length < 2) return;

    let index = Number(root.dataset.lookIndex || 0);
    if (!Number.isFinite(index) || index < 0 || index >= images.length) {
      index = 0;
    }

    const preloadCache = new Map();
    const preload = (url) => {
      if (!url) return Promise.resolve();
      if (preloadCache.has(url)) return preloadCache.get(url);

      const promise = new Promise((resolve) => {
        const image = new Image();
        image.decoding = "async";
        image.onload = image.onerror = () => resolve();
        image.src = url;
      });
      preloadCache.set(url, promise);
      return promise;
    };

    images.forEach((image) => {
      preload(image.src);
      preload(image.thumb || image.src);
    });

    let applying = false;

    const apply = async () => {
      const current = images[index];
      const next = images[(index + 1) % images.length];
      if (!current || !next || applying) return;

      applying = true;
      await preload(current.src);
      await preload(next.thumb || next.src);

      main.src = current.src;
      main.removeAttribute("srcset");
      main.alt = current.alt || "";
      if (typeof main.decode === "function") {
        try {
          await main.decode();
        } catch (error) {
          // ignore decode errors for cached swaps
        }
      }

      thumbImg.src = next.thumb || next.src;
      thumbImg.removeAttribute("srcset");
      thumbImg.alt = next.alt || "";

      root.dataset.lookIndex = String(index);
      applying = false;
    };

    thumbBtn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (applying) return;
      index = (index + 1) % images.length;
      apply();
    });

    root.dataset.lookCycleBound = "true";
  });
};

document.addEventListener("DOMContentLoaded", initLookImageCycles);

// venice, ca live clock
const initVeniceClock = () => {
  const el = document.querySelector("[data-venice-clock]");
  if (!el) return;

  const getPart = (parts, type) =>
    parts.find((part) => part.type === type)?.value || "";

  const updateClock = () => {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).formatToParts(new Date());

    const weekday = getPart(parts, "weekday");
    const day = getPart(parts, "day");
    const month = getPart(parts, "month");
    const hour = getPart(parts, "hour");
    const minute = getPart(parts, "minute");
    const dayPeriod = getPart(parts, "dayPeriod").toLowerCase();

    el.textContent = `Venice, CA. ${weekday} ${day} ${month}. ${hour}:${minute}${dayPeriod}`;
  };

  updateClock();
  setInterval(updateClock, 1000);
};

document.addEventListener("DOMContentLoaded", initVeniceClock);

// header menu: hover and scroll background
const initHeaderMenuBackground = () => {
  const menu = document.querySelector("[data-header-menu]");
  if (!menu) return;

  const syncMenuBackground = () => {
    const filterModal = document.querySelector("[data-filter-modal]");
    const miniCartModal = document.querySelector("[data-mini-cart-modal]");
    const searchModal = document.querySelector("[data-search-modal]");
    const localizationModal = document.querySelector(
      "[data-localization-modal]",
    );
    const isFilterModalOpen =
      filterModal && !filterModal.classList.contains("hidden");
    const isMiniCartOpen =
      miniCartModal && !miniCartModal.classList.contains("hidden");
    const isSearchModalOpen =
      searchModal && !searchModal.classList.contains("hidden");
    const isLocalizationModalOpen =
      localizationModal && !localizationModal.classList.contains("hidden");
    menu.classList.toggle(
      "bg-gray-light",
      window.scrollY > 0 ||
        isFilterModalOpen ||
        isMiniCartOpen ||
        isSearchModalOpen ||
        isLocalizationModalOpen,
    );
  };

  syncMenuBackground();
  window.addEventListener("scroll", syncMenuBackground, { passive: true });
  document.addEventListener("filters:modal:toggled", syncMenuBackground);
  document.addEventListener("cart:modal:toggled", syncMenuBackground);
  document.addEventListener("search:modal:toggled", syncMenuBackground);
  document.addEventListener("localization:modal:toggled", syncMenuBackground);
};

document.addEventListener("DOMContentLoaded", initHeaderMenuBackground);

const initHeaderChromeScrollFade = () => {
  const headerClock = document.querySelector("[data-header-clock]");
  const headerLocations = document.querySelector("[data-header-locations]");
  if (!headerClock && !headerLocations) return;

  const fadeTargets = [headerClock, headerLocations].filter(Boolean);
  let lastY = window.scrollY;
  let isHidden = false;

  const setHiddenState = (hidden) => {
    if (isHidden === hidden) return;
    isHidden = hidden;
    fadeTargets.forEach((el) => {
      el.classList.toggle("opacity-0", hidden);
    });
  };

  const onScroll = () => {
    const currentY = window.scrollY;
    const delta = currentY - lastY;

    if (currentY <= 20) {
      setHiddenState(false);
      lastY = currentY;
      return;
    }

    if (delta > 2) {
      setHiddenState(true);
    } else if (delta < -2) {
      setHiddenState(false);
    }

    lastY = currentY;
  };

  window.addEventListener("scroll", onScroll, { passive: true });
};

document.addEventListener("DOMContentLoaded", initHeaderChromeScrollFade);

// position header dialogs under menu on desktop, above on mobile
const syncHeaderMenuDialogLayout = (dialog, menu) => {
  if (!dialog || !menu) return;

  const rect = menu.getBoundingClientRect();
  const gap = 10;
  const isMobile = window.matchMedia("(max-width: 767px)").matches;

  dialog.style.width = `${rect.width}px`;

  if (isMobile) {
    const available = Math.max(120, rect.top - gap * 2);
    const mobileCap = Math.round(window.innerHeight * 0.5);
    dialog.style.top = "auto";
    dialog.style.bottom = `${Math.max(gap, window.innerHeight - rect.top + gap)}px`;
    dialog.style.marginTop = "0px";
    dialog.style.maxHeight = `${Math.min(available, mobileCap)}px`;
    return;
  }

  dialog.style.bottom = "auto";
  dialog.style.top = `${rect.bottom}px`;
  dialog.style.marginTop = "";
  dialog.style.maxHeight = "";
};

const initCollectionFilterModal = () => {
  const modal = document.querySelector("[data-filter-modal]");
  const openButton = document.querySelector("[data-filter-open]");
  const menu = document.querySelector("[data-header-menu]");
  const dialog = modal?.querySelector("[data-filter-dialog]");
  if (!modal || !openButton || !dialog) return;

  const closeButtons = [...modal.querySelectorAll("[data-filter-close]")];
  const toggleButtons = [...modal.querySelectorAll("[data-filter-toggle]")];
  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let previousFocusedElement = null;
  let closeTimeoutId = null;
  const isModalOpen = () => openButton.getAttribute("aria-expanded") === "true";

  const setGroupState = (group, open) => {
    const button = group.querySelector("[data-filter-toggle]");
    const panel = group.querySelector("[data-filter-panel]");
    const icon = group.querySelector("[data-filter-icon]");
    if (!button) return;

    button.setAttribute("aria-expanded", String(open));

    if (open) {
      group.setAttribute("data-open", "");
    } else {
      group.removeAttribute("data-open");
    }

    if (panel) setCollapsiblePanelHidden(panel, !open);

    const svg = icon?.querySelector("svg");
    if (svg) svg.classList.toggle("rotate-180", open);
  };

  const closeOtherGroups = (activeGroup) => {
    modal.querySelectorAll("[data-filter-group]").forEach((group) => {
      if (group !== activeGroup) setGroupState(group, false);
    });
  };

  const toggleGroup = (button) => {
    const group = button.closest("[data-filter-group]");
    if (!group) return;

    const expanded = button.getAttribute("aria-expanded") === "true";
    const nextState = !expanded;

    if (nextState) closeOtherGroups(group);
    setGroupState(group, nextState);
  };

  const trapFocus = (event) => {
    if (event.key !== "Tab") return;
    const focusable = [...modal.querySelectorAll(focusableSelector)].filter(
      (element) =>
        !element.hasAttribute("hidden") &&
        !element.closest('[aria-hidden="true"]'),
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const syncFilterDialogLayout = () => {
    syncHeaderMenuDialogLayout(dialog, menu);
  };

  const openModal = () => {
    if (closeTimeoutId) {
      window.clearTimeout(closeTimeoutId);
      closeTimeoutId = null;
    }

    document.dispatchEvent(
      new CustomEvent("header:menu:close-overlays", {
        detail: { except: "filters" },
      }),
    );
    previousFocusedElement = document.activeElement;
    syncFilterDialogLayout();
    modal.classList.remove("hidden");
    requestAnimationFrame(() => {
      modal.classList.remove("opacity-0", "pointer-events-none");
      modal.classList.add("opacity-100");
    });
    modal.setAttribute("aria-hidden", "false");
    openButton.setAttribute("aria-expanded", "true");
    document.dispatchEvent(new CustomEvent("filters:modal:toggled"));

    const firstFocusable = modal.querySelector(focusableSelector);
    firstFocusable?.focus();
  };

  const closeModal = () => {
    modal.classList.remove("opacity-100");
    modal.classList.add("opacity-0", "pointer-events-none");
    modal.setAttribute("aria-hidden", "true");
    openButton.setAttribute("aria-expanded", "false");
    document.dispatchEvent(new CustomEvent("filters:modal:toggled"));

    closeTimeoutId = window.setTimeout(() => {
      modal.classList.add("hidden");
      closeTimeoutId = null;
    }, 300);

    if (previousFocusedElement instanceof HTMLElement) {
      previousFocusedElement.focus();
    } else {
      openButton.focus();
    }
  };

  const toggleModal = () => {
    if (isModalOpen()) {
      closeModal();
      return;
    }
    openModal();
  };

  openButton.addEventListener("click", toggleModal);
  closeButtons.forEach((button) =>
    button.addEventListener("click", closeModal),
  );
  document.addEventListener("header:menu:close-overlays", (event) => {
    if (event.detail?.except === "filters" || !isModalOpen()) return;
    closeModal();
  });
  toggleButtons.forEach((button) => {
    button.addEventListener("click", () => toggleGroup(button));
  });

  document.addEventListener("keydown", (event) => {
    if (modal.classList.contains("hidden")) return;
    if (event.key === "Escape") {
      closeModal();
      return;
    }
    trapFocus(event);
  });

  window.addEventListener("resize", () => {
    if (modal.classList.contains("hidden")) return;
    syncFilterDialogLayout();
  });
};

document.addEventListener("DOMContentLoaded", initCollectionFilterModal);

const initLocalizationModal = () => {
  const modal = document.querySelector("[data-localization-modal]");
  const openButtons = [
    ...document.querySelectorAll("[data-localization-open]"),
  ];
  const menu = document.querySelector("[data-header-menu]");
  const dialog = modal?.querySelector("[data-localization-dialog]");
  if (!modal || openButtons.length === 0 || !dialog) return;

  const closeButtons = [...modal.querySelectorAll("[data-localization-close]")];
  const toggleButton = modal.querySelector("[data-localization-toggle]");
  const group = modal.querySelector("[data-localization-group]");
  const panel = modal.querySelector("[data-localization-panel]");
  const icon = modal.querySelector("[data-localization-icon]");
  const countryLabel = modal.querySelector("[data-localization-country-label]");
  const currencyLabel = modal.querySelector(
    "[data-localization-currency-label]",
  );
  const countryInputs = [
    ...modal.querySelectorAll('input[name="country_code"]'),
  ];
  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let previousFocusedElement = null;
  let closeTimeoutId = null;
  const isModalOpen = () =>
    openButtons.some(
      (button) => button.getAttribute("aria-expanded") === "true",
    );

  const setOpenButtonsExpanded = (expanded) => {
    openButtons.forEach((button) => {
      button.setAttribute("aria-expanded", String(expanded));
    });
  };

  const syncSelectionLabels = (input) => {
    if (!(input instanceof HTMLInputElement)) return;
    if (countryLabel)
      countryLabel.textContent = input.dataset.countryName || "";
    if (currencyLabel) {
      currencyLabel.textContent = input.dataset.currencyLabel || "";
    }
  };

  const setPanelOpen = (nextState) => {
    if (!toggleButton || !group || !panel) return;

    toggleButton.setAttribute("aria-expanded", String(nextState));
    if (nextState) {
      group.setAttribute("data-open", "");
    } else {
      group.removeAttribute("data-open");
    }
    setCollapsiblePanelHidden(panel, !nextState);

    const svg = icon?.querySelector("svg");
    if (svg) svg.classList.toggle("rotate-180", nextState);
  };

  const trapFocus = (event) => {
    if (event.key !== "Tab") return;
    const focusable = [...modal.querySelectorAll(focusableSelector)].filter(
      (element) =>
        !element.hasAttribute("hidden") &&
        !element.closest('[aria-hidden="true"]'),
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const syncLocalizationDialogLayout = () => {
    syncHeaderMenuDialogLayout(dialog, menu);
  };

  const openModal = () => {
    if (closeTimeoutId) {
      window.clearTimeout(closeTimeoutId);
      closeTimeoutId = null;
    }

    document.dispatchEvent(
      new CustomEvent("header:menu:close-overlays", {
        detail: { except: "localization" },
      }),
    );
    previousFocusedElement = document.activeElement;
    syncLocalizationDialogLayout();
    modal.classList.remove("hidden");
    requestAnimationFrame(() => {
      modal.classList.remove("opacity-0", "pointer-events-none");
      modal.classList.add("opacity-100");
    });
    modal.setAttribute("aria-hidden", "false");
    setOpenButtonsExpanded(true);
    document.dispatchEvent(new CustomEvent("localization:modal:toggled"));

    const firstFocusable = modal.querySelector(focusableSelector);
    firstFocusable?.focus();
  };

  const closeModal = () => {
    setPanelOpen(false);
    modal.classList.remove("opacity-100");
    modal.classList.add("opacity-0", "pointer-events-none");
    modal.setAttribute("aria-hidden", "true");
    setOpenButtonsExpanded(false);
    document.dispatchEvent(new CustomEvent("localization:modal:toggled"));

    closeTimeoutId = window.setTimeout(() => {
      modal.classList.add("hidden");
      closeTimeoutId = null;
    }, 300);

    if (previousFocusedElement instanceof HTMLElement) {
      previousFocusedElement.focus();
    } else {
      openButtons[0]?.focus();
    }
  };

  const toggleModal = () => {
    if (isModalOpen()) {
      closeModal();
      return;
    }
    openModal();
  };

  openButtons.forEach((button) => {
    button.addEventListener("click", toggleModal);
  });
  closeButtons.forEach((button) => {
    button.addEventListener("click", closeModal);
  });
  document.addEventListener("header:menu:close-overlays", (event) => {
    if (event.detail?.except === "localization" || !isModalOpen()) return;
    closeModal();
  });
  toggleButton?.addEventListener("click", () => {
    const expanded = toggleButton.getAttribute("aria-expanded") === "true";
    setPanelOpen(!expanded);
  });
  countryInputs.forEach((input) => {
    input.addEventListener("change", () => {
      syncSelectionLabels(input);
      setPanelOpen(false);
    });
  });

  let typeAheadBuffer = "";
  let typeAheadTimer = null;

  const jumpToCountry = (char) => {
    const isOpen = group?.hasAttribute("data-open");
    if (!isOpen) setPanelOpen(true);

    typeAheadBuffer += char;
    clearTimeout(typeAheadTimer);
    typeAheadTimer = setTimeout(() => {
      typeAheadBuffer = "";
    }, 600);

    const match = countryInputs.find((input) =>
      (input.dataset.countryName || "")
        .toLowerCase()
        .startsWith(typeAheadBuffer),
    );
    if (match) {
      match.checked = true;
      syncSelectionLabels(match);
      const li = match.closest("li");
      if (li) li.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  };

  document.addEventListener("keydown", (event) => {
    if (modal.classList.contains("hidden")) return;
    if (event.key === "Escape") {
      closeModal();
      return;
    }
    if (
      event.key.length === 1 &&
      /[a-z]/i.test(event.key) &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      event.preventDefault();
      jumpToCountry(event.key.toLowerCase());
      return;
    }
    trapFocus(event);
  });

  window.addEventListener("resize", () => {
    if (modal.classList.contains("hidden")) return;
    syncLocalizationDialogLayout();
  });
};

document.addEventListener("DOMContentLoaded", initLocalizationModal);

const initMiniCartModal = () => {
  const modal = document.querySelector("[data-mini-cart-modal]");
  const openButton = document.querySelector("[data-cart-open]");
  const menu = document.querySelector("[data-header-menu]");
  const dialog = modal?.querySelector("[data-cart-dialog]");
  if (!modal || !openButton || !dialog) return;

  const closeButtons = [...modal.querySelectorAll("[data-cart-close]")];
  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let previousFocusedElement = null;
  let closeTimeoutId = null;

  const trapFocus = (event) => {
    if (event.key !== "Tab") return;
    const focusable = [...modal.querySelectorAll(focusableSelector)].filter(
      (element) =>
        !element.hasAttribute("hidden") &&
        !element.closest('[aria-hidden="true"]'),
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const syncCartDialogLayout = () => {
    syncHeaderMenuDialogLayout(dialog, menu);
  };

  const lockScroll = () => {
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden");
  };

  const isModalOpen = () => openButton.getAttribute("aria-expanded") === "true";

  const openModal = () => {
    if (closeTimeoutId) {
      window.clearTimeout(closeTimeoutId);
      closeTimeoutId = null;
    }

    document.dispatchEvent(
      new CustomEvent("header:menu:close-overlays", {
        detail: { except: "cart" },
      }),
    );
    previousFocusedElement = document.activeElement;
    syncCartDialogLayout();
    modal.classList.remove("hidden");
    // double raf so opacity-0 paints before fade-in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.classList.remove("opacity-0", "pointer-events-none");
        modal.classList.add("opacity-100");
      });
    });
    modal.setAttribute("aria-hidden", "false");
    openButton.setAttribute("aria-expanded", "true");
    lockScroll();
    document.dispatchEvent(new CustomEvent("cart:modal:toggled"));

    const firstFocusable = modal.querySelector(focusableSelector);
    firstFocusable?.focus();
  };

  const closeModal = () => {
    modal.classList.remove("opacity-100");
    modal.classList.add("opacity-0", "pointer-events-none");
    modal.setAttribute("aria-hidden", "true");
    openButton.setAttribute("aria-expanded", "false");
    unlockScroll();
    document.dispatchEvent(new CustomEvent("cart:modal:toggled"));

    closeTimeoutId = window.setTimeout(() => {
      modal.classList.add("hidden");
      closeTimeoutId = null;
    }, 300);

    if (previousFocusedElement instanceof HTMLElement) {
      previousFocusedElement.focus();
    } else {
      openButton.focus();
    }
  };

  const toggleModal = () => {
    if (isModalOpen()) {
      closeModal();
      return;
    }
    openModal();
  };

  openButton.addEventListener("click", toggleModal);
  document.addEventListener("cart:open", openModal);
  document.addEventListener("header:menu:close-overlays", (event) => {
    if (event.detail?.except === "cart" || !isModalOpen()) return;
    closeModal();
  });
  closeButtons.forEach((button) =>
    button.addEventListener("click", closeModal),
  );

  modal.addEventListener("click", (event) => {
    const quantityButton = event.target.closest("[data-mini-cart-quantity]");
    if (quantityButton instanceof HTMLButtonElement) {
      const key = quantityButton.dataset.key;
      const quantity = Number(quantityButton.dataset.quantity);
      if (key && !Number.isNaN(quantity)) {
        changeMiniCartQuantity(key, quantity);
      }
      return;
    }

    const removeButton = event.target.closest("[data-mini-cart-remove]");
    if (removeButton instanceof HTMLButtonElement) {
      const key = removeButton.dataset.key;
      if (key) changeMiniCartQuantity(key, 0);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (modal.classList.contains("hidden")) return;
    if (event.key === "Escape") {
      closeModal();
      return;
    }
    trapFocus(event);
  });

  window.addEventListener("resize", () => {
    if (modal.classList.contains("hidden")) return;
    syncCartDialogLayout();
  });
};

document.addEventListener("DOMContentLoaded", initMiniCartModal);

const initAccessibilityModal = () => {
  const modal = document.querySelector("[data-accessibility-modal]");
  const dialog = modal?.querySelector("[data-accessibility-dialog]");
  if (!modal || !dialog) return;

  const closeButtons = [
    ...modal.querySelectorAll("[data-accessibility-close]"),
  ];
  const toggleAllButton = modal.querySelector(
    "[data-accessibility-toggle-all]",
  );
  const resetButton = modal.querySelector("[data-accessibility-reset]");
  const accessibilityOptions = [
    {
      id: "black-and-white",
      className: "accessibility-black-and-white",
      storageKey: "accessibility-black-and-white",
    },
    {
      id: "high-contrast",
      className: "accessibility-high-contrast",
      storageKey: "accessibility-high-contrast",
    },
    {
      id: "image-invert",
      className: "accessibility-image-invert",
      storageKey: "accessibility-image-invert",
    },
    {
      id: "reduce-motion",
      className: "accessibility-reduce-motion",
      storageKey: "accessibility-reduce-motion",
    },
    {
      id: "increase-font-size",
      className: "accessibility-increase-font-size",
      storageKey: "accessibility-increase-font-size",
    },
  ];
  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let previousFocusedElement = null;
  let closeTimeoutId = null;
  let isOpen = false;

  const getOptionButton = (id) =>
    modal.querySelector(`[data-accessibility-option="${id}"]`);

  const isOptionEnabled = (option) =>
    document.documentElement.classList.contains(option.className);

  const areAllOptionsEnabled = () =>
    accessibilityOptions.every((option) => isOptionEnabled(option));

  const syncToggleAllLabel = () => {
    if (!toggleAllButton) return;
    toggleAllButton.textContent = areAllOptionsEnabled()
      ? "Turn off all"
      : "Turn on all";
  };

  // clear legacy remove-all-styling preference and restore stylesheets
  try {
    window.localStorage.removeItem("accessibility-remove-all-styling");
  } catch (error) {
    // ignore storage errors
  }
  document.documentElement.classList.remove("accessibility-remove-all-styling");
  document.querySelectorAll("[data-accessibility-style-prev]").forEach((el) => {
    const prev = el.getAttribute("data-accessibility-style-prev");
    if (prev !== null) el.disabled = prev === "1";
    el.removeAttribute("data-accessibility-style-prev");
  });

  const setOption = (option, enabled) => {
    document.documentElement.classList.toggle(option.className, enabled);

    const button = getOptionButton(option.id);
    if (button) {
      button.setAttribute("aria-pressed", String(enabled));
      const dot = button.querySelector("[data-accessibility-option-dot]");
      dot?.classList.toggle("bg-black", enabled);
    }

    try {
      window.localStorage.setItem(option.storageKey, enabled ? "1" : "0");
    } catch (error) {
      // ignore storage errors
    }

    syncToggleAllLabel();
  };

  accessibilityOptions.forEach((option) => {
    try {
      setOption(option, window.localStorage.getItem(option.storageKey) === "1");
    } catch (error) {
      setOption(option, false);
    }

    getOptionButton(option.id)?.addEventListener("click", () => {
      setOption(option, !isOptionEnabled(option));
    });
  });

  toggleAllButton?.addEventListener("click", () => {
    const enableAll = !areAllOptionsEnabled();
    accessibilityOptions.forEach((option) => setOption(option, enableAll));
  });

  resetButton?.addEventListener("click", () => {
    accessibilityOptions.forEach((option) => setOption(option, false));
  });

  const trapFocus = (event) => {
    if (event.key !== "Tab") return;
    const focusable = [...modal.querySelectorAll(focusableSelector)].filter(
      (element) =>
        !element.hasAttribute("hidden") &&
        !element.closest('[aria-hidden="true"]'),
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const lockScroll = () => {
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden");
  };

  const openModal = () => {
    if (closeTimeoutId) {
      window.clearTimeout(closeTimeoutId);
      closeTimeoutId = null;
    }

    document.dispatchEvent(
      new CustomEvent("header:menu:close-overlays", {
        detail: { except: "accessibility" },
      }),
    );
    previousFocusedElement = document.activeElement;
    modal.classList.remove("hidden");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.classList.remove("opacity-0", "pointer-events-none");
        modal.classList.add("opacity-100");
      });
    });
    modal.setAttribute("aria-hidden", "false");
    isOpen = true;
    lockScroll();

    const firstFocusable = modal.querySelector(focusableSelector);
    firstFocusable?.focus();
  };

  const closeModal = () => {
    modal.classList.remove("opacity-100");
    modal.classList.add("opacity-0", "pointer-events-none");
    modal.setAttribute("aria-hidden", "true");
    isOpen = false;
    unlockScroll();

    closeTimeoutId = window.setTimeout(() => {
      modal.classList.add("hidden");
      closeTimeoutId = null;
    }, 300);

    if (previousFocusedElement instanceof HTMLElement) {
      previousFocusedElement.focus();
    }
  };

  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[href]");
    if (!(link instanceof HTMLAnchorElement)) return;

    const href = link.getAttribute("href") || "";
    if (href !== "#accessibility" && !href.endsWith("#accessibility")) return;

    event.preventDefault();
    if (isOpen) {
      closeModal();
      return;
    }
    openModal();
  });

  document.addEventListener("header:menu:close-overlays", (event) => {
    if (event.detail?.except === "accessibility" || !isOpen) return;
    closeModal();
  });

  closeButtons.forEach((button) =>
    button.addEventListener("click", closeModal),
  );

  document.addEventListener("keydown", (event) => {
    if (modal.classList.contains("hidden")) return;
    if (event.key === "Escape") {
      closeModal();
      return;
    }
    trapFocus(event);
  });
};

document.addEventListener("DOMContentLoaded", initAccessibilityModal);

const initSearchModal = () => {
  const modal = document.querySelector("[data-search-modal]");
  const openButton = document.querySelector("[data-search-open]");
  const menu = document.querySelector("[data-header-menu]");
  const content = modal?.querySelector("[data-search-content]");
  const searchInput = modal?.querySelector("[data-search-input]");
  const collectionsGrid = modal?.querySelector("[data-search-collections]");
  const resultsGrid = modal?.querySelector("[data-search-results]");
  const menusEl = modal?.querySelector("[data-search-menus]");
  const menuArea = modal?.querySelector("[data-search-menu-area]");
  const suggestionsEl = modal?.querySelector("[data-search-suggestions]");
  const suggestionsWrap = modal?.querySelector(
    "[data-search-suggestions-wrap]",
  );
  const emptyMessage = modal?.querySelector("[data-search-empty]");
  const noResultsTemplate =
    emptyMessage?.dataset.template ||
    "No results found for “TERMS_PLACEHOLDER”";
  const headerLogo = document.querySelector("[data-header-logo]");
  const logoFull = headerLogo?.querySelector("[data-logo-full]");
  const logoMini = headerLogo?.querySelector("[data-logo-mini]");
  const headerClock = document.querySelector("[data-header-clock]");
  const headerLocations = document.querySelector("[data-header-locations]");
  const filterOpen = document.querySelector("[data-filter-open]");
  if (!modal || !openButton) return;

  const closeButtons = [...modal.querySelectorAll("[data-search-close]")];
  let previousFocusedElement = null;
  let closeTimeoutId = null;
  let searchTimeoutId = null;
  let searchAbortController = null;
  const isModalOpen = () => openButton.getAttribute("aria-expanded") === "true";

  const syncSearchModalLayout = () => {
    if (!menu || !content) return;

    const menuRect = menu.getBoundingClientRect();
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const form = searchInput?.closest("form");

    if (isMobile) {
      const logo = document.querySelector("[data-header-logo]");
      const logoBottom = logo?.getBoundingClientRect().bottom || 72;
      const bottomGap = Math.max(24, window.innerHeight - menuRect.top + 16);

      content.style.paddingTop = `${logoBottom + 16}px`;
      content.style.paddingBottom = `${bottomGap}px`;
      content.style.width = "";

      // full width within content padding — don't shrink to menu width
      form?.style.removeProperty("width");
      form?.style.removeProperty("max-width");
      emptyMessage?.style.removeProperty("width");
      emptyMessage?.style.removeProperty("max-width");
      suggestionsWrap?.style.removeProperty("width");
      suggestionsWrap?.style.removeProperty("max-width");
      return;
    }

    const menuHorizontalPadding = 40;
    const searchWidth = Math.max(0, menuRect.width - menuHorizontalPadding);
    content.style.width = "";
    content.style.paddingTop = `${menuRect.bottom + 10}px`;
    content.style.paddingBottom = "";
    form?.style.setProperty("width", `${searchWidth}px`);
    form?.style.setProperty("max-width", `${searchWidth}px`);
    emptyMessage?.style.setProperty("width", `${searchWidth}px`);
    emptyMessage?.style.setProperty("max-width", `${searchWidth}px`);
    suggestionsWrap?.style.setProperty("width", `${searchWidth}px`);
    suggestionsWrap?.style.setProperty("max-width", `${searchWidth}px`);
  };

  const showSearchHeader = () => {
    headerClock?.classList.add("invisible");
    headerLocations?.classList.add("invisible");
    filterOpen?.classList.add("opacity-0", "pointer-events-none");
    if (!headerLogo) return;
    headerLogo.classList.add("z-[60]");
    logoFull?.classList.add("hidden");
    logoMini?.classList.remove("hidden");
  };

  const restoreHeaderChrome = () => {
    headerClock?.classList.remove("invisible");
    headerLocations?.classList.remove("invisible");
    filterOpen?.classList.remove("opacity-0", "pointer-events-none");
    if (!headerLogo) return;
    headerLogo.classList.remove("z-[60]");
    if (logoFull && !logoFull.hasAttribute("data-default-hidden")) {
      logoFull.classList.remove("hidden");
      logoMini?.classList.add("hidden");
      return;
    }
    logoMini?.classList.remove("hidden");
    logoFull?.classList.add("hidden");
  };

  if (logoFull?.classList.contains("hidden")) {
    logoFull.setAttribute("data-default-hidden", "");
  }
  if (logoMini?.classList.contains("hidden")) {
    logoMini.setAttribute("data-default-hidden", "");
  }

  const hideEmptyMessage = () => {
    if (!emptyMessage) return;
    emptyMessage.textContent = "\u00a0";
    emptyMessage.classList.add("invisible", "opacity-0");
    emptyMessage.setAttribute("aria-hidden", "true");
  };

  const showEmptyMessage = (query) => {
    if (!emptyMessage) return;
    emptyMessage.textContent = noResultsTemplate.replace(
      "TERMS_PLACEHOLDER",
      query,
    );
    emptyMessage.classList.remove("invisible", "opacity-0");
    emptyMessage.setAttribute("aria-hidden", "false");
  };

  const showDefaultMenus = () => {
    menuArea?.classList.remove("hidden");
    menusEl?.classList.remove("hidden");
    suggestionsWrap?.classList.add("hidden");
    if (suggestionsEl) suggestionsEl.innerHTML = "";
  };

  const applySuggestionsColumns = () => {
    const panel = suggestionsEl?.querySelector(
      "[data-search-suggestions-panel]",
    );
    if (!panel || !suggestionsEl) return;

    panel.classList.remove("columns-2", "gap-x-8");
    panel.style.columnFill = "";

    if (window.matchMedia("(max-width: 767px)").matches) return;

    if (panel.scrollHeight > suggestionsEl.clientHeight + 1) {
      panel.classList.add("columns-2", "gap-x-8");
      panel.style.columnFill = "auto";
    }
  };

  const showSuggestionLinks = (markup) => {
    if (!markup) {
      showDefaultMenus();
      return;
    }

    menuArea?.classList.add("hidden");
    menusEl?.classList.add("hidden");
    if (!suggestionsEl || !suggestionsWrap) return;

    suggestionsEl.innerHTML = markup;
    suggestionsWrap.classList.remove("hidden");
    requestAnimationFrame(applySuggestionsColumns);
  };

  const showCollections = () => {
    collectionsGrid?.classList.remove("hidden");
    resultsGrid?.classList.add("hidden");
    if (resultsGrid) resultsGrid.innerHTML = "";
    showDefaultMenus();
  };

  const showResults = ({ productsMarkup, linksMarkup }) => {
    if (resultsGrid) {
      if (productsMarkup) {
        resultsGrid.innerHTML = productsMarkup;
        resultsGrid.classList.remove("hidden");
        collectionsGrid?.classList.add("hidden");
      } else {
        resultsGrid.innerHTML = "";
        resultsGrid.classList.add("hidden");
        collectionsGrid?.classList.remove("hidden");
      }
    }
    showSuggestionLinks(linksMarkup);
    if (productsMarkup || linksMarkup) hideEmptyMessage();
  };

  const fetchPredictiveSearch = async (query) => {
    if (searchAbortController) searchAbortController.abort();
    searchAbortController = new AbortController();

    const params = new URLSearchParams({
      q: query,
      "resources[type]": "product,collection,page,article",
      "resources[limit]": "7",
      "resources[limit_scope]": "each",
      section_id: "predictive-search",
    });

    const response = await fetch(
      `${window.Shopify?.routes?.root || "/"}search/suggest?${params}`,
      { signal: searchAbortController.signal },
    );
    if (!response.ok) throw new Error("search request failed");

    const text = await response.text();
    const section = new DOMParser()
      .parseFromString(text, "text/html")
      .querySelector("#shopify-section-predictive-search");
    const results = section?.querySelector("[data-predictive-search-results]");
    const productsMarkup =
      results
        ?.querySelector("[data-predictive-search-products]")
        ?.innerHTML?.trim() || "";
    const linksMarkup =
      results
        ?.querySelector("[data-predictive-search-links]")
        ?.innerHTML?.trim() || "";

    return { productsMarkup, linksMarkup };
  };

  const handleSearchInput = (event) => {
    const query = event.target.value.trim();

    if (searchTimeoutId) window.clearTimeout(searchTimeoutId);

    if (!query) {
      if (searchAbortController) searchAbortController.abort();
      hideEmptyMessage();
      showCollections();
      return;
    }

    searchTimeoutId = window.setTimeout(async () => {
      try {
        const { productsMarkup, linksMarkup } =
          await fetchPredictiveSearch(query);
        if (productsMarkup || linksMarkup) {
          showResults({ productsMarkup, linksMarkup });
          return;
        }
        collectionsGrid?.classList.remove("hidden");
        resultsGrid?.classList.add("hidden");
        if (resultsGrid) resultsGrid.innerHTML = "";
        showDefaultMenus();
        showEmptyMessage(query);
      } catch (error) {
        if (error?.name === "AbortError") return;
        collectionsGrid?.classList.remove("hidden");
        resultsGrid?.classList.add("hidden");
        if (resultsGrid) resultsGrid.innerHTML = "";
        showDefaultMenus();
        showEmptyMessage(query);
      }
    }, 300);
  };

  const resetSearch = () => {
    if (searchTimeoutId) window.clearTimeout(searchTimeoutId);
    if (searchAbortController) searchAbortController.abort();
    if (searchInput) searchInput.value = "";
    hideEmptyMessage();
    showCollections();
  };

  const lockScroll = () => {
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden");
  };

  const openModal = () => {
    if (closeTimeoutId) {
      window.clearTimeout(closeTimeoutId);
      closeTimeoutId = null;
    }

    document.dispatchEvent(
      new CustomEvent("header:menu:close-overlays", {
        detail: { except: "search" },
      }),
    );
    previousFocusedElement = document.activeElement;
    syncSearchModalLayout();
    showSearchHeader();
    modal.classList.remove("hidden");
    requestAnimationFrame(() => {
      modal.classList.remove("opacity-0", "pointer-events-none");
      modal.classList.add("opacity-100");
    });
    modal.setAttribute("aria-hidden", "false");
    openButton.setAttribute("aria-expanded", "true");
    lockScroll();
    document.dispatchEvent(new CustomEvent("search:modal:toggled"));
    searchInput?.focus();
  };

  const closeModal = () => {
    modal.classList.remove("opacity-100");
    modal.classList.add("opacity-0", "pointer-events-none");
    modal.setAttribute("aria-hidden", "true");
    openButton.setAttribute("aria-expanded", "false");
    restoreHeaderChrome();
    resetSearch();
    unlockScroll();
    document.dispatchEvent(new CustomEvent("search:modal:toggled"));

    closeTimeoutId = window.setTimeout(() => {
      modal.classList.add("hidden");
      closeTimeoutId = null;
    }, 300);

    if (previousFocusedElement instanceof HTMLElement) {
      previousFocusedElement.focus();
    } else {
      openButton.focus();
    }
  };

  openButton.addEventListener("click", () => {
    if (modal.classList.contains("hidden")) {
      openModal();
      return;
    }
    closeModal();
  });
  closeButtons.forEach((button) =>
    button.addEventListener("click", closeModal),
  );
  document.addEventListener("header:menu:close-overlays", (event) => {
    if (event.detail?.except === "search" || !isModalOpen()) return;
    closeModal();
  });
  searchInput?.addEventListener("input", handleSearchInput);

  document.addEventListener("keydown", (event) => {
    if (modal.classList.contains("hidden")) return;
    if (event.key === "Escape") closeModal();
  });

  window.addEventListener("resize", () => {
    if (modal.classList.contains("hidden")) return;
    syncSearchModalLayout();
    if (suggestionsWrap && !suggestionsWrap.classList.contains("hidden")) {
      applySuggestionsColumns();
    }
  });
};

document.addEventListener("DOMContentLoaded", initSearchModal);

// products list: scroll-driven active item
class ProductsList {
  constructor(section) {
    this.section = section;
    this.triggers = [
      ...section.querySelectorAll("[data-product-scroll-trigger]"),
    ];
    if (this.triggers.length === 0) return;

    this.activeIndex = 0;
    this.scrollActiveIndex = 0;
    this.ticking = false;
    this.isHoverLocked = false;
    this.onScroll = this.onScroll.bind(this);

    const hoverSelector =
      "[data-product-item].product-list-media, [data-product-item].product-list-text";
    const hoverItems = [...section.querySelectorAll(hoverSelector)];

    hoverItems.forEach((item) => {
      const index = Number(item.dataset.productItem);
      if (!Number.isFinite(index)) return;

      item.addEventListener("mouseenter", () => {
        this.isHoverLocked = true;
        this.setActive(index);
      });

      item.addEventListener("mouseleave", (event) => {
        const related = event.relatedTarget;
        if (related instanceof Element && related.closest(hoverSelector))
          return;

        this.isHoverLocked = false;
        this.setActive(this.scrollActiveIndex);
      });
    });

    section.addEventListener("mouseleave", () => {
      if (!this.isHoverLocked) return;

      this.isHoverLocked = false;
      this.setActive(this.scrollActiveIndex);
    });

    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onScroll, { passive: true });
    this.updateActive();
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.updateActive();
    });
  }

  setActive(index) {
    if (index === this.activeIndex) return;

    this.activeIndex = index;
    this.section.setAttribute("data-active", String(index));
  }

  updateActive() {
    if (this.isHoverLocked) return;

    const activationLine = window.innerHeight * 0.35;
    let nextIndex = this.activeIndex;
    let closestDistance = Infinity;
    let hasVisibleTrigger = false;

    this.triggers.forEach((trigger, index) => {
      const rect = trigger.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;

      hasVisibleTrigger = true;
      const center = rect.top + rect.height / 2;
      const distance = Math.abs(center - activationLine);

      if (distance < closestDistance) {
        closestDistance = distance;
        nextIndex = index;
      }
    });

    if (!hasVisibleTrigger) {
      const sectionRect = this.section.getBoundingClientRect();

      if (sectionRect.bottom < 0) {
        nextIndex = this.triggers.length - 1;
      } else if (sectionRect.top > window.innerHeight) {
        nextIndex = 0;
      } else {
        return;
      }
    }

    this.scrollActiveIndex = nextIndex;
    this.setActive(nextIndex);
  }
}

const initProductsLists = (scope = document) => {
  scope.querySelectorAll("[data-products-list]").forEach((section) => {
    if (section.productsList) return;
    section.productsList = new ProductsList(section);
  });
};

const formatAudioDuration = (seconds) => {
  if (!seconds || !Number.isFinite(seconds)) return "";

  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  return [hours, minutes, secs]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
};

const getOddCenteredIndex = (length) => {
  if (length <= 0) return 0;
  if (length % 2 === 1) return Math.floor(length / 2);

  const leftCandidate = length / 2 - 1;
  const rightCandidate = length / 2;
  return (leftCandidate + 1) % 2 === 1 ? leftCandidate : rightCandidate;
};

class ListWheel {
  constructor({
    viewport,
    listRoot,
    itemAttr,
    heightVar,
    fadeVar,
    onStep,
    onWheelEnd,
    onSelect,
    initialIndex,
    alignToItemEdges = false,
  }) {
    this.viewport = viewport;
    this.listRoot = listRoot;
    this.itemAttr = itemAttr;
    this.heightVar = heightVar;
    this.fadeVar = fadeVar;
    this.onStep = onStep;
    this.onWheelEnd = onWheelEnd;
    this.onSelect = onSelect;
    this.alignToItemEdges = alignToItemEdges;
    this.abortController = new AbortController();
    this.wheelAccumulator = 0;
    this.wheelStep = 40;
    this.wheelEndTimer = null;
    this.wheelCooldown = false;
    this.isWheeling = false;
    this.pendingStepDelta = 0;
    this.stepRafId = null;
    this.wheelTransitionDuration = 160;
    this.transitionDuration = 280;
    this.transitionEasing = "cubic-bezier(0.22, 1, 0.36, 1)";
    this.pointerId = null;
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.pointerLastY = 0;
    this.pointerDragging = false;
    this.pointerMoved = false;
    this.touchAccumulator = 0;
    this.itemHeight = 0;
    this.rowHeight = 0;
    this.contentHeight = 0;
    this.itemCount = 0;
    this.activeIndex = 0;
    this.visualIndex = 0;
    this.copyCount = 7;
    this.safeMin = 0;
    this.safeMax = 0;

    const originalItems = [...listRoot.querySelectorAll(":scope > li")];
    this.itemCount = originalItems.length;
    if (this.itemCount === 0) return;

    const { signal } = this.abortController;
    this.setupInfiniteTrack(originalItems);
    this.safeMin = this.itemCount;
    this.safeMax = this.itemCount * (this.copyCount - 1) - 1;

    listRoot.addEventListener(
      "click",
      (event) => {
        const link = event.target.closest(`[${itemAttr}]`);
        if (!link) return;

        const index = Number(link.getAttribute(itemAttr));
        if (Number.isFinite(index)) {
          this.setActive(index, true, true);
        }
      },
      { signal },
    );

    listRoot.addEventListener(
      "focusin",
      (event) => {
        const link = event.target.closest(`[${itemAttr}]`);
        if (!link) return;

        const index = Number(link.getAttribute(itemAttr));
        if (Number.isFinite(index)) {
          this.setActive(index, true, true);
        }
      },
      { signal },
    );

    this.resizeObserver = new ResizeObserver(() => {
      this.measure();
      this.updatePosition(false);
    });
    this.resizeObserver.observe(viewport);

    this.bindWheelNavigation();
    this.bindPointerNavigation();
    if (Number.isFinite(initialIndex)) {
      this.activeIndex = Math.min(
        Math.max(0, Math.floor(initialIndex)),
        this.itemCount - 1,
      );
    } else {
      this.activeIndex = getOddCenteredIndex(this.itemCount);
    }
    this.visualIndex = this.safeMin + this.activeIndex;
    this.measure();
    this.updatePosition(false);
  }

  setupInfiniteTrack(originalItems) {
    if (this.itemCount <= 1) return;

    const fragment = document.createDocumentFragment();

    for (let copy = 0; copy < this.copyCount; copy += 1) {
      originalItems.forEach((item, index) => {
        const clone = item.cloneNode(true);
        const link = clone.querySelector(`[${this.itemAttr}]`);
        if (link) {
          link.setAttribute(this.itemAttr, String(index));
        }
        fragment.appendChild(clone);
      });
    }

    this.listRoot.replaceChildren(fragment);
  }

  destroy() {
    clearTimeout(this.wheelEndTimer);
    if (this.stepRafId !== null) {
      cancelAnimationFrame(this.stepRafId);
      this.stepRafId = null;
    }
    this.wheelCooldown = false;
    this.isWheeling = false;
    this.pendingStepDelta = 0;
    this.resizeObserver?.disconnect();
    this.abortController.abort();
  }

  measure() {
    const items = [...this.listRoot.querySelectorAll(":scope > li")];
    if (items.length === 0) return;

    const firstItem = items[0];
    const lastInCopy = items[Math.min(this.itemCount, items.length) - 1];

    this.rowHeight = firstItem.offsetHeight;
    this.itemHeight =
      items.length > 1
        ? items[1].offsetTop - items[0].offsetTop
        : this.rowHeight;
    this.contentHeight =
      lastInCopy.offsetTop + lastInCopy.offsetHeight - firstItem.offsetTop;
  }

  getActiveRow() {
    if (!this.itemHeight) return 0;

    const rows = Math.max(
      1,
      Math.round(
        (this.contentHeight || this.viewport.clientHeight) / this.itemHeight,
      ),
    );
    return Math.floor((rows - 1) / 2);
  }

  applySharedMetrics({ rowHeight, stride, contentHeight }) {
    this.rowHeight = rowHeight;
    this.itemHeight = stride;
    this.contentHeight = contentHeight;

    this.listRoot.querySelectorAll(":scope > li").forEach((item) => {
      item.style.minHeight = `${stride}px`;
    });

    this.viewport.style.setProperty(this.heightVar, `${rowHeight}px`);
    const varPrefix = this.heightVar.replace(/-item-height$/, "");
    this.viewport.style.setProperty(`${varPrefix}-item-stride`, `${stride}px`);
    this.viewport.style.setProperty(
      `${varPrefix}-active-row`,
      String(this.getActiveRow()),
    );
    if (this.fadeVar) {
      this.viewport.style.setProperty(
        this.fadeVar,
        `${Math.round(stride * 2)}px`,
      );
    }
    this.viewport.style.height = `${contentHeight}px`;
    this.updatePosition(false);
  }

  getOffset() {
    const activeItem = this.getItem(this.visualIndex);
    if (!activeItem) return 0;

    const viewportHeight = this.viewport.clientHeight;

    // snap so viewport edges land on item boundaries (no half rows)
    if (this.alignToItemEdges && this.itemHeight) {
      return activeItem.offsetTop - this.getActiveRow() * this.itemHeight;
    }

    const itemCenter = activeItem.offsetTop + activeItem.offsetHeight / 2;
    return itemCenter - viewportHeight / 2;
  }

  getItem(index) {
    return this.listRoot.querySelectorAll(":scope > li")[index];
  }

  bindWheelNavigation() {
    const { signal } = this.abortController;
    this.viewport.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();

        if (this.wheelCooldown) return;

        if (!this.isWheeling) {
          this.isWheeling = true;
        }

        clearTimeout(this.wheelEndTimer);
        this.wheelEndTimer = setTimeout(() => {
          this.finishWheeling();
        }, 80);

        this.wheelAccumulator += event.deltaY;

        while (Math.abs(this.wheelAccumulator) >= this.wheelStep) {
          const direction = this.wheelAccumulator > 0 ? 1 : -1;
          this.wheelAccumulator -= this.wheelStep * direction;
          this.pendingStepDelta += direction;
        }

        this.scheduleStep();
      },
      { passive: false, signal },
    );
  }

  bindPointerNavigation() {
    const { signal } = this.abortController;
    const dragThreshold = 8;

    this.viewport.style.touchAction = "none";

    this.viewport.addEventListener(
      "pointerdown",
      (event) => {
        if (this.itemCount <= 1) return;
        if (event.isPrimary === false) return;
        if (event.pointerType === "mouse" && event.button !== 0) return;

        this.pointerId = event.pointerId;
        this.pointerStartX = event.clientX;
        this.pointerStartY = event.clientY;
        this.pointerLastY = event.clientY;
        this.pointerDragging = false;
        this.pointerMoved = false;
        this.touchAccumulator = 0;
      },
      { signal },
    );

    this.viewport.addEventListener(
      "pointermove",
      (event) => {
        if (this.pointerId !== event.pointerId) return;
        if (this.wheelCooldown) return;

        const dx = event.clientX - this.pointerStartX;
        const dy = event.clientY - this.pointerStartY;

        if (!this.pointerDragging) {
          if (Math.abs(dx) < dragThreshold && Math.abs(dy) < dragThreshold) {
            return;
          }

          // leave horizontal gestures to the page
          if (Math.abs(dx) > Math.abs(dy)) {
            this.pointerId = null;
            return;
          }

          this.pointerDragging = true;
          this.pointerMoved = true;
          this.isWheeling = true;

          try {
            this.viewport.setPointerCapture(event.pointerId);
          } catch (error) {
            // ignore capture failures
          }
        }

        event.preventDefault();

        const deltaY = event.clientY - this.pointerLastY;
        this.pointerLastY = event.clientY;
        // finger up matches wheel down
        this.touchAccumulator += -deltaY;

        const stepSize = Math.max(24, (this.itemHeight || 40) * 0.55);
        while (Math.abs(this.touchAccumulator) >= stepSize) {
          const direction = this.touchAccumulator > 0 ? 1 : -1;
          this.touchAccumulator -= stepSize * direction;
          this.pendingStepDelta += direction;
        }

        this.scheduleStep();
      },
      { passive: false, signal },
    );

    const endPointer = (event) => {
      if (this.pointerId !== event.pointerId) return;

      const wasDragging = this.pointerDragging;
      this.pointerId = null;
      this.pointerDragging = false;
      this.touchAccumulator = 0;

      if (wasDragging) {
        this.finishWheeling();
        window.setTimeout(() => {
          this.pointerMoved = false;
        }, 400);
      }
    };

    this.viewport.addEventListener("pointerup", endPointer, { signal });
    this.viewport.addEventListener("pointercancel", endPointer, { signal });

    this.viewport.addEventListener(
      "click",
      (event) => {
        if (!this.pointerMoved) return;
        event.preventDefault();
        event.stopPropagation();
        this.pointerMoved = false;
      },
      { capture: true, signal },
    );
  }

  flushAnimation() {
    this.listRoot.style.transition = "none";
    this.listRoot.style.transform = `translate3d(0, ${this.getCurrentTranslateY()}px, 0)`;
    void this.listRoot.offsetHeight;
  }

  scheduleStep() {
    if (this.pendingStepDelta === 0 || this.stepRafId !== null) return;

    this.stepRafId = requestAnimationFrame(() => {
      this.stepRafId = null;
      this.applyPendingSteps();
    });
  }

  applyPendingSteps() {
    const delta = this.pendingStepDelta;
    this.pendingStepDelta = 0;
    if (delta === 0 || this.itemCount <= 1) return;

    this.flushAnimation();

    const direction = delta > 0 ? 1 : -1;
    const steps = Math.abs(delta);

    for (let step = 0; step < steps; step += 1) {
      this.visualIndex += direction;
      this.activeIndex =
        (this.activeIndex + direction + this.itemCount) % this.itemCount;
      this.onStep?.(this.activeIndex);
    }

    this.hardClampVisualIndex();
    this.maintainSafeZone();
    this.updatePosition(true);
  }

  getCopyHeight() {
    const first = this.getItem(0);
    const firstInNextCopy = this.getItem(this.itemCount);

    if (first && firstInNextCopy) {
      return firstInNextCopy.offsetTop - first.offsetTop;
    }

    if (!this.itemHeight) {
      this.measure();
    }

    return this.itemCount * this.itemHeight;
  }

  getCurrentTranslateY() {
    const transform = getComputedStyle(this.listRoot).transform;
    const matrix =
      transform === "none" ? new DOMMatrix() : new DOMMatrix(transform);

    return matrix.m42;
  }

  repositionCopies(copies) {
    if (!copies || this.itemCount <= 1) return;

    this.visualIndex += copies * this.itemCount;

    const copyHeight = this.getCopyHeight();
    this.listRoot.style.transition = "none";
    this.listRoot.style.transform = `translate3d(0, ${this.getCurrentTranslateY() - copies * copyHeight}px, 0)`;
    void this.listRoot.offsetHeight;
  }

  maintainSafeZone() {
    while (this.visualIndex > this.safeMax) {
      this.repositionCopies(-1);
    }

    while (this.visualIndex < this.safeMin) {
      this.repositionCopies(1);
    }
  }

  hardClampVisualIndex() {
    if (this.itemCount <= 1) return false;

    const hardMax = this.itemCount * this.copyCount;
    let jumped = false;

    while (this.visualIndex < 0) {
      this.repositionCopies(1);
      jumped = true;
    }

    while (this.visualIndex >= hardMax) {
      this.repositionCopies(-1);
      jumped = true;
    }

    return jumped;
  }

  normalizeVisualIndex() {
    if (this.itemCount <= 1) return false;

    const before = this.visualIndex;
    this.maintainSafeZone();

    return before !== this.visualIndex;
  }

  finishWheeling() {
    if (this.stepRafId !== null) {
      cancelAnimationFrame(this.stepRafId);
      this.stepRafId = null;
    }

    if (this.pendingStepDelta !== 0) {
      this.applyPendingSteps();
    }

    this.wheelAccumulator = 0;
    this.pendingStepDelta = 0;
    this.isWheeling = false;
    this.wheelCooldown = true;

    this.hardClampVisualIndex();
    this.maintainSafeZone();
    this.updatePosition(true);
    this.onWheelEnd?.();

    setTimeout(() => {
      this.wheelCooldown = false;
    }, 120);
  }

  stepActive(direction) {
    if (this.itemCount <= 1) return;

    this.pendingStepDelta += direction;
    this.scheduleStep();
  }

  updatePosition(animate) {
    if (!this.itemHeight) {
      this.measure();
    }
    if (!this.itemHeight) return;

    const duration = this.isWheeling
      ? this.wheelTransitionDuration
      : this.transitionDuration;

    this.listRoot.style.transition = animate
      ? `transform ${duration}ms ${this.transitionEasing}`
      : "none";
    this.listRoot.style.transform = `translate3d(0, ${-this.getOffset()}px, 0)`;
  }

  syncTo(index, animate = true) {
    this.setActive(index, animate, false);
  }

  setActive(index, animate = true, notify = false) {
    if (index < 0 || index >= this.itemCount) return;

    this.activeIndex = index;

    if (this.itemCount <= 1) {
      this.visualIndex = index;
      this.updatePosition(false);
      if (notify) {
        this.onSelect?.(index);
      }
      return;
    }

    const currentCopy = Math.floor(this.visualIndex / this.itemCount);
    const safeCopy = Math.min(Math.max(currentCopy, 1), this.copyCount - 2);
    this.visualIndex = safeCopy * this.itemCount + index;
    this.updatePosition(animate);

    if (!animate) {
      this.maintainSafeZone();
      this.updatePosition(false);
    }

    if (notify) {
      this.onSelect?.(index);
    }
  }
}

class CollectionsList {
  constructor(section) {
    this.section = section;
    this.abortController = new AbortController();
    this.wheels = [];
    this.syncing = false;
    this.leadWheel = null;
    this.archivePairIndex = 0;
    this.archiveApplying = false;
    this.archivePreloadCache = new Map();

    const columns = [
      ...section.querySelectorAll("[data-collections-wheel-column]"),
    ];

    columns.forEach((column) => {
      const viewport = column.querySelector(
        "[data-collections-wheel-viewport]",
      );
      const listRoot = column.querySelector("[data-collections-wheel-list]");
      if (!viewport || !listRoot) return;

      const wheel = new ListWheel({
        viewport,
        listRoot,
        itemAttr: "data-collection-item",
        heightVar: "--collections-wheel-item-height",
        fadeVar: "--collections-wheel-fade-size",
        initialIndex: 0,
        alignToItemEdges: true,
        onStep: (index) => this.handleColumnStep(wheel, index),
        onWheelEnd: () => this.handleColumnWheelEnd(wheel),
        onSelect: (index) => this.handleColumnSelect(wheel, index),
      });

      if (wheel.itemCount) {
        this.wheels.push(wheel);
      }
    });

    if (this.wheels.length === 0) return;

    this.alignRowHeights();
    this.activeIndex = this.wheels[0].activeIndex;
    this.section.setAttribute("data-active", String(this.activeIndex));
    this.initArchiveGallery();
    this.showArchivePair(this.activeIndex, 0);
  }

  destroy() {
    this.wheels.forEach((wheel) => wheel.destroy());
    this.abortController.abort();
    delete this.section.collectionsList;
  }

  initArchiveGallery() {
    const root = this.section.querySelector(
      "[data-collections-archive-gallery]",
    );
    const dataEl = root?.querySelector("[data-archive-galleries]");
    this.archiveMedia = root?.querySelector("[data-archive-media]");
    this.archiveLeft = root?.querySelector("[data-archive-left]");
    this.archiveRight = root?.querySelector("[data-archive-right]");
    this.archiveThumbBtn = root?.querySelector("[data-archive-thumb]");
    this.archiveThumbImg = root?.querySelector("[data-archive-thumb-img]");
    this.archiveGalleries = [];
    this.archiveCollectionIndex = 0;
    this.archiveFallbackLeft = root?.dataset.archiveFallbackLeft
      ? { src: root.dataset.archiveFallbackLeft, alt: "" }
      : null;
    this.archiveFallbackRight = root?.dataset.archiveFallbackRight
      ? { src: root.dataset.archiveFallbackRight, alt: "" }
      : null;

    if (!dataEl || !this.archiveLeft || !this.archiveRight) return;

    try {
      this.archiveGalleries = JSON.parse(dataEl.textContent);
    } catch (error) {
      this.archiveGalleries = [];
    }

    if (!Array.isArray(this.archiveGalleries)) {
      this.archiveGalleries = [];
    }

    this.archiveGalleries.forEach((entry) => {
      (entry?.images || []).forEach((image) => {
        this.preloadArchiveImage(image?.src);
        this.preloadArchiveImage(image?.thumb || image?.src);
      });
    });

    this.archiveThumbBtn?.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.archiveThumbSide === "prev") {
          this.retreatArchivePair();
        } else {
          this.advanceArchivePair();
        }
      },
      { signal: this.abortController.signal },
    );

    this.archiveMedia?.addEventListener(
      "mousemove",
      (event) => this.moveArchiveThumb(event),
      { signal: this.abortController.signal },
    );

    this.archiveMedia?.addEventListener(
      "mouseenter",
      (event) => this.moveArchiveThumb(event),
      { signal: this.abortController.signal },
    );

    this.archiveMedia?.addEventListener(
      "mouseleave",
      () => {
        this.archivePointer = null;
      },
      { signal: this.abortController.signal },
    );

    window.addEventListener(
      "scroll",
      () => this.syncArchiveThumbToPointer(),
      { signal: this.abortController.signal, passive: true },
    );

    window.addEventListener(
      "resize",
      () => this.syncArchiveThumbToPointer(),
      { signal: this.abortController.signal, passive: true },
    );

    this.archiveLeft?.addEventListener(
      "click",
      (event) => {
        if (this.getArchiveImages(this.archiveCollectionIndex).length <= 1) {
          return;
        }
        event.preventDefault();
        if (this.isArchiveMobile()) {
          this.advanceArchivePair();
        } else {
          this.retreatArchivePair();
        }
      },
      { signal: this.abortController.signal },
    );

    this.archiveRight?.addEventListener(
      "click",
      (event) => {
        if (this.isArchiveMobile()) return;
        if (this.getArchiveImages(this.archiveCollectionIndex).length <= 1) {
          return;
        }
        event.preventDefault();
        this.advanceArchivePair();
      },
      { signal: this.abortController.signal },
    );

    this.archiveMobileQuery = window.matchMedia("(max-width: 767px)");
    const onArchiveViewportChange = () => {
      this.resetArchiveThumbPosition();
      this.showArchivePair(
        this.archiveCollectionIndex,
        this.archivePairIndex || 0,
      );
    };
    if (typeof this.archiveMobileQuery.addEventListener === "function") {
      this.archiveMobileQuery.addEventListener(
        "change",
        onArchiveViewportChange,
      );
      this.abortController.signal.addEventListener("abort", () => {
        this.archiveMobileQuery.removeEventListener(
          "change",
          onArchiveViewportChange,
        );
      });
    } else if (typeof this.archiveMobileQuery.addListener === "function") {
      this.archiveMobileQuery.addListener(onArchiveViewportChange);
    }
  }

  moveArchiveThumb(event) {
    if (this.isArchiveMobile() || !this.archiveMedia || !this.archiveThumbBtn) {
      return;
    }
    if (this.archiveThumbBtn.hasAttribute("hidden")) return;

    this.archivePointer = {
      x: event.clientX,
      y: event.clientY,
    };
    this.syncArchiveThumbToPointer();
    this.updateArchiveThumbPreview(event.clientX);
  }

  syncArchiveThumbToPointer() {
    if (this.isArchiveMobile() || !this.archiveMedia || !this.archiveThumbBtn) {
      return;
    }
    if (!this.archivePointer || this.archiveThumbBtn.hasAttribute("hidden")) {
      return;
    }

    const rect = this.archiveMedia.getBoundingClientRect();
    const { x, y } = this.archivePointer;
    const inside =
      x >= rect.left &&
      x <= rect.right &&
      y >= rect.top &&
      y <= rect.bottom;

    if (!inside) return;

    this.archiveThumbBtn.style.left = `${x - rect.left}px`;
    this.archiveThumbBtn.style.top = `${y - rect.top}px`;
  }

  resetArchiveThumbPosition() {
    if (!this.archiveThumbBtn) return;
    this.archivePointer = null;
    this.archiveThumbSide = null;
    this.archiveThumbBtn.style.left = "";
    this.archiveThumbBtn.style.top = "";
  }

  getArchiveThumbForSide(side) {
    const images = this.getArchiveImages(this.archiveCollectionIndex);
    const count = images.length;
    if (count < 2) return null;

    const index = this.archivePairIndex || 0;
    if (side === "prev") {
      return images[(index - 1 + count) % count];
    }
    return images[(index + 2) % count];
  }

  updateArchiveThumbPreview(clientX) {
    if (this.isArchiveMobile() || !this.archiveMedia || !this.archiveThumbImg) {
      return;
    }

    const rect = this.archiveMedia.getBoundingClientRect();
    if (!rect.width) return;

    const side = clientX < rect.left + rect.width / 2 ? "prev" : "next";
    if (this.archiveThumbSide === side) return;

    this.archiveThumbSide = side;
    const image = this.getArchiveThumbForSide(side);
    if (!image) return;

    this.setArchiveImage(this.archiveThumbImg, {
      src: image.thumb || image.src,
      alt: image.alt || "",
    });
  }

  isArchiveMobile() {
    return this.archiveMobileQuery?.matches ?? window.innerWidth < 768;
  }

  preloadArchiveImage(url) {
    if (!url) return Promise.resolve();
    if (this.archivePreloadCache.has(url)) {
      return this.archivePreloadCache.get(url);
    }

    const promise = new Promise((resolve) => {
      const image = new Image();
      image.decoding = "async";
      image.onload = image.onerror = () => resolve();
      image.src = url;
    });
    this.archivePreloadCache.set(url, promise);
    return promise;
  }

  getArchiveImages(collectionIndex) {
    const entry = this.archiveGalleries[collectionIndex];
    const images = entry?.images;
    return Array.isArray(images) ? images.filter((image) => image?.src) : [];
  }

  setArchiveImage(el, image) {
    if (!el) return;

    if (!image?.src) {
      el.hidden = true;
      el.removeAttribute("src");
      el.removeAttribute("srcset");
      return;
    }

    el.hidden = false;
    el.src = image.src;
    el.removeAttribute("srcset");
    el.alt = image.alt || "";
  }

  async showArchivePair(collectionIndex, pairIndex = 0) {
    this.archiveCollectionIndex = collectionIndex;
    const images = this.getArchiveImages(collectionIndex);
    const canCycle = images.length > 1;

    if (!canCycle) {
      this.archiveMedia?.removeAttribute("data-archive-cycleable");
      this.archiveThumbBtn?.classList.add("hidden");
      this.archiveThumbBtn?.setAttribute("hidden", "");
      this.resetArchiveThumbPosition();
      this.archiveLeft?.parentElement?.classList.remove("cursor-pointer");
      this.setArchiveImage(this.archiveLeft, this.archiveFallbackLeft);
      this.setArchiveImage(this.archiveRight, this.archiveFallbackRight);
      this.archivePairIndex = 0;
      return;
    }

    const count = images.length;
    const index = ((pairIndex % count) + count) % count;
    const isMobile = this.isArchiveMobile();
    const left = images[index];
    const right = images[(index + 1) % count];
    const thumbNext = isMobile ? right : images[(index + 2) % count];
    const thumbPrev = images[(index - 1 + count) % count];
    this.archiveLeft?.parentElement?.classList.toggle(
      "cursor-pointer",
      isMobile,
    );
    this.archiveMedia?.toggleAttribute("data-archive-cycleable", !isMobile);

    this.archiveApplying = true;
    await Promise.all([
      this.preloadArchiveImage(left.src),
      this.preloadArchiveImage(right.src),
      this.preloadArchiveImage(thumbNext.thumb || thumbNext.src),
      this.preloadArchiveImage(thumbPrev.thumb || thumbPrev.src),
    ]);

    this.setArchiveImage(this.archiveLeft, left);
    this.setArchiveImage(this.archiveRight, right);

    this.archivePairIndex = index;
    let thumbSide = "next";
    if (this.archivePointer && this.archiveMedia) {
      const mediaRect = this.archiveMedia.getBoundingClientRect();
      if (
        mediaRect.width &&
        this.archivePointer.x < mediaRect.left + mediaRect.width / 2
      ) {
        thumbSide = "prev";
      }
    }
    this.archiveThumbSide = thumbSide;
    const thumb = thumbSide === "prev" ? thumbPrev : thumbNext;
    this.setArchiveImage(this.archiveThumbImg, {
      src: thumb.thumb || thumb.src,
      alt: thumb.alt || "",
    });

    if (typeof this.archiveLeft?.decode === "function") {
      try {
        await this.archiveLeft.decode();
      } catch (error) {
        // ignore decode errors for cached swaps
      }
    }
    if (!isMobile && typeof this.archiveRight?.decode === "function") {
      try {
        await this.archiveRight.decode();
      } catch (error) {
        // ignore decode errors for cached swaps
      }
    }

    this.archiveThumbBtn?.classList.remove("hidden");
    this.archiveThumbBtn?.removeAttribute("hidden");
    this.archiveApplying = false;
  }

  advanceArchivePair() {
    if (this.archiveApplying) return;

    const images = this.getArchiveImages(this.archiveCollectionIndex);
    if (images.length < 2) return;

    this.showArchivePair(
      this.archiveCollectionIndex,
      (this.archivePairIndex + 1) % images.length,
    );
  }

  retreatArchivePair() {
    if (this.archiveApplying) return;

    const images = this.getArchiveImages(this.archiveCollectionIndex);
    if (images.length < 2) return;

    const count = images.length;
    this.showArchivePair(
      this.archiveCollectionIndex,
      (this.archivePairIndex - 1 + count) % count,
    );
  }

  alignRowHeights() {
    let maxRowHeight = 0;
    let maxStride = 0;

    this.wheels.forEach((wheel) => {
      wheel.measure();
      maxRowHeight = Math.max(maxRowHeight, wheel.rowHeight);
      maxStride = Math.max(maxStride, wheel.itemHeight);
    });

    const itemCount = this.wheels[0]?.itemCount || 1;
    const metrics = {
      rowHeight: maxRowHeight,
      stride: maxStride,
      contentHeight: maxStride * itemCount,
    };

    this.wheels.forEach((wheel) => {
      wheel.applySharedMetrics(metrics);
    });
  }

  getColumnElement(wheel) {
    return wheel.listRoot.closest("[data-collections-wheel-column]");
  }

  setLeadColumn(leadWheel) {
    this.wheels.forEach((wheel) => {
      this.getColumnElement(wheel)?.classList.toggle(
        "is-leading",
        wheel === leadWheel,
      );
    });
  }

  clearLeadColumn() {
    this.wheels.forEach((wheel) => {
      this.getColumnElement(wheel)?.classList.remove("is-leading");
    });
    this.section.removeAttribute("data-preview-active");
  }

  handleColumnStep(sourceWheel, index) {
    this.leadWheel = sourceWheel;
    this.section.setAttribute("data-preview-active", String(index));
    this.setLeadColumn(sourceWheel);
  }

  handleColumnWheelEnd(sourceWheel) {
    if (this.leadWheel !== sourceWheel) return;

    this.activeIndex = sourceWheel.activeIndex;
    this.section.setAttribute("data-active", String(this.activeIndex));
    this.clearLeadColumn();
    this.syncFollowers(sourceWheel);
    this.showArchivePair(this.activeIndex, 0);
    this.leadWheel = null;
  }

  handleColumnSelect(sourceWheel, index) {
    this.leadWheel = null;
    this.clearLeadColumn();
    this.activeIndex = index;
    this.section.setAttribute("data-active", String(index));
    this.syncFollowers(sourceWheel);
    this.showArchivePair(this.activeIndex, 0);
  }

  syncFollowers(leadWheel) {
    if (this.syncing) return;

    this.syncing = true;
    this.wheels.forEach((wheel) => {
      if (wheel !== leadWheel) {
        wheel.syncTo(this.activeIndex, true);
      }
    });
    this.syncing = false;
  }

  setActive(index, options = {}) {
    const { animate = false } = options;
    if (index < 0 || index >= this.wheels[0]?.itemCount) return;

    this.leadWheel = null;
    this.clearLeadColumn();
    this.activeIndex = index;
    this.section.setAttribute("data-active", String(index));

    this.syncing = true;
    this.wheels.forEach((wheel) => wheel.syncTo(index, animate));
    this.syncing = false;
    this.showArchivePair(this.activeIndex, 0);
  }
}

const initCollectionsLists = (scope = document) => {
  scope.querySelectorAll("[data-collections-list]").forEach((section) => {
    if (section.collectionsList) {
      section.collectionsList.destroy();
    }

    section.collectionsList = new CollectionsList(section);
  });
};

// sounds list: wheel-select track and load audio
class SoundsList {
  constructor(section) {
    this.section = section;
    this.wheels = [];
    this.syncing = false;
    this.leadWheel = null;

    const columns = [...section.querySelectorAll("[data-sounds-wheel-column]")];
    const titleList = columns[0]?.querySelector("[data-sounds-wheel-list]");
    this.audioUrls = titleList
      ? [...titleList.querySelectorAll("[data-audio-url]")].map((button) =>
          button.dataset.audioUrl?.trim(),
        )
      : [];

    columns.forEach((column) => {
      const viewport = column.querySelector("[data-sounds-wheel-viewport]");
      const listRoot = column.querySelector("[data-sounds-wheel-list]");
      if (!viewport || !listRoot) return;

      const wheel = new ListWheel({
        viewport,
        listRoot,
        itemAttr: "data-sound-item",
        heightVar: "--sounds-wheel-item-height",
        initialIndex: 0,
        alignToItemEdges: true,
        onStep: (index) => this.handleColumnStep(wheel, index),
        onWheelEnd: () => this.handleColumnWheelEnd(wheel),
        onSelect: (index) => this.handleColumnSelect(wheel, index),
      });

      if (wheel.itemCount) {
        this.wheels.push(wheel);
      }
    });

    if (this.wheels.length === 0) return;

    this.alignRowHeights();
    this.activeIndex = this.wheels[0].activeIndex;
    this.section.setAttribute("data-active", String(this.activeIndex));

    this.ensurePlayer();
    this.preloadDurations();
    this.loadActiveAudio(false);
  }

  destroy() {
    this.wheels.forEach((wheel) => wheel.destroy());
    window.ProductAudio?.pauseAudioPlayersIn?.(this.section);
    delete this.section.soundsList;
  }

  ensurePlayer() {
    window.ProductAudio?.initAudioPlayers?.(this.section);
    this.playerRoot = this.section.querySelector("[data-audio-player]");
    return this.playerRoot?.audioPlayer;
  }

  getDurationColumn() {
    return this.section.querySelector("[data-sounds-wheel-column]:last-child");
  }

  setDuration(index, seconds) {
    const durationColumn = this.getDurationColumn();
    if (!durationColumn) return;

    durationColumn
      .querySelectorAll(`[data-sound-item="${index}"] [data-sound-duration]`)
      .forEach((durationEl) => {
        durationEl.textContent = formatAudioDuration(seconds);
      });
  }

  preloadDurations() {
    this.audioUrls.forEach((url, index) => {
      if (!url) return;

      const audio = new Audio();
      audio.preload = "metadata";

      audio.addEventListener("loadedmetadata", () => {
        this.setDuration(index, audio.duration);
      });

      audio.src = url;
    });
  }

  loadActiveAudio(pause = true) {
    if (pause) {
      window.ProductAudio?.pauseAudioPlayersIn?.(this.section);
    }

    const url = this.audioUrls[this.activeIndex];
    const player = this.ensurePlayer();

    if (!player || !url) return;

    player.load(url).then(() => {
      const wavesurfer = player.wavesurfer;
      if (wavesurfer) {
        this.setDuration(this.activeIndex, wavesurfer.getDuration());
      }
    });
  }

  alignRowHeights() {
    let maxRowHeight = 0;
    let maxStride = 0;

    this.wheels.forEach((wheel) => {
      wheel.measure();
      maxRowHeight = Math.max(maxRowHeight, wheel.rowHeight);
      maxStride = Math.max(maxStride, wheel.itemHeight);
    });

    const itemCount = this.wheels[0]?.itemCount || 1;
    const metrics = {
      rowHeight: maxRowHeight,
      stride: maxStride,
      contentHeight: maxStride * itemCount,
    };

    this.wheels.forEach((wheel) => {
      wheel.applySharedMetrics(metrics);
    });
  }

  getColumnElement(wheel) {
    return wheel.listRoot.closest("[data-sounds-wheel-column]");
  }

  setLeadColumn(leadWheel) {
    this.wheels.forEach((wheel) => {
      this.getColumnElement(wheel)?.classList.toggle(
        "is-leading",
        wheel === leadWheel,
      );
    });
  }

  clearLeadColumn() {
    this.wheels.forEach((wheel) => {
      this.getColumnElement(wheel)?.classList.remove("is-leading");
    });
    this.section.removeAttribute("data-preview-active");
  }

  handleColumnStep(sourceWheel, index) {
    this.leadWheel = sourceWheel;
    this.section.setAttribute("data-preview-active", String(index));
    this.setLeadColumn(sourceWheel);
  }

  handleColumnWheelEnd(sourceWheel) {
    if (this.leadWheel !== sourceWheel) return;

    this.activeIndex = sourceWheel.activeIndex;
    this.section.setAttribute("data-active", String(this.activeIndex));
    this.clearLeadColumn();
    this.syncFollowers(sourceWheel);
    this.leadWheel = null;
    this.loadActiveAudio(true);
  }

  handleColumnSelect(sourceWheel, index) {
    this.leadWheel = null;
    this.clearLeadColumn();
    this.activeIndex = index;
    this.section.setAttribute("data-active", String(index));
    this.syncFollowers(sourceWheel);
    this.loadActiveAudio(true);
  }

  syncFollowers(leadWheel) {
    if (this.syncing) return;

    this.syncing = true;
    this.wheels.forEach((wheel) => {
      if (wheel !== leadWheel) {
        wheel.syncTo(this.activeIndex, true);
      }
    });
    this.syncing = false;
  }
}

const initSoundsLists = (scope = document) => {
  whenProductAudioReady(() => {
    window.ProductAudio.initAudioPlayers(scope);

    scope.querySelectorAll("[data-sounds-list]").forEach((section) => {
      if (section.soundsList) {
        section.soundsList.destroy();
      }

      section.soundsList = new SoundsList(section);
    });
  });
};

const formatAudioRemaining = (seconds) => {
  if (!seconds || !Number.isFinite(seconds)) return "-00:00";

  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;

  return `-${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const formatAudioDurationShort = (seconds) => {
  if (!seconds || !Number.isFinite(seconds)) return "00:00";

  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;

  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

class FullTeasers {
  constructor(section) {
    this.section = section;
    this.abortController = new AbortController();
    this.items = [...section.querySelectorAll("[data-full-teaser-item]")];
    this.mobileItems = [
      ...section.querySelectorAll("[data-full-teaser-mobile-item]"),
    ];
    this.panels = [...section.querySelectorAll("[data-full-teaser-panel]")];
    this.mobileAudio = section.querySelector("[data-full-teaser-audio-mobile]");
    this.headerMenu = document.querySelector("[data-header-menu]");
    this.mediaQuery = window.matchMedia("(min-width: 768px)");
    this.controlRoots = [
      ...section.querySelectorAll("[data-full-teaser-video-controls]"),
    ];
    this.boundVideo = null;
    this.userMuted = true;
    this.userPaused = false;
    this.mobileObserver = null;

    if (this.items.length === 0 && this.mobileItems.length === 0) return;

    const { signal } = this.abortController;
    this.activeIndex = 0;
    this.hoverIndex = null;
    this.slideCount = Math.max(this.items.length, this.mobileItems.length);

    this.section.querySelectorAll("video").forEach((video) => {
      video.autoplay = false;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
    });

    this.items.forEach((item, index) => {
      item.addEventListener("click", () => this.setActive(index), { signal });
      item.addEventListener("mouseenter", () => this.setHover(index), {
        signal,
      });
      item.addEventListener("mouseleave", () => this.setHover(null), {
        signal,
      });
    });

    this.bindControlRoots(signal);

    this.onViewportChange = () => {
      this.syncMobileAudioLayout();
      this.setupMobileSnapScroll();
      this.setupMobileObserver();
      this.syncVideos();
      this.bindActiveVideo();
    };
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener("change", this.onViewportChange, {
        signal,
      });
    } else {
      this.mediaQuery.addListener(this.onViewportChange);
    }

    window.addEventListener("resize", () => this.syncMobileAudioLayout(), {
      signal,
      passive: true,
    });

    this.syncMobileAudioLayout();
    this.setupMobileSnapScroll();
    this.setupMobileObserver();

    const hashIndex = this.getIndexFromHash();
    this.setActive(hashIndex >= 0 ? hashIndex : 0, false);
  }

  isDesktop() {
    return this.mediaQuery.matches;
  }

  getSourceItem(index) {
    return this.items[index] || this.mobileItems[index] || null;
  }

  getActiveVideo() {
    if (this.isDesktop()) {
      return (
        this.panels[this.activeIndex]?.querySelector("video") || null
      );
    }

    return this.mobileItems[this.activeIndex]?.querySelector("video") || null;
  }

  getIndexFromHash() {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return -1;

    const sources = this.items.length ? this.items : this.mobileItems;
    return sources.findIndex((item) => item.dataset.teaserHandle === hash);
  }

  destroy() {
    this.abortController.abort();
    if (this.mediaQuery.removeListener && this.onViewportChange) {
      this.mediaQuery.removeListener(this.onViewportChange);
    }
    this.unbindActiveVideo();
    window.ProductAudio?.destroyVideoWaves?.(this.controlRoots);
    this.mobileObserver?.disconnect();
    this.teardownMobileSnapScroll();
    this.syncMobileSnapScroll(false);
    this.pauseAllVideos();
    delete this.section.fullTeasers;
  }

  bindControlRoots(signal) {
    this.controlRoots.forEach((root) => {
      const playButton = root.querySelector("[data-audio-play]");
      const muteButton = root.querySelector("[data-audio-mute]");
      const seekTrack = root.querySelector("[data-audio-wave]");

      playButton?.addEventListener("click", () => this.togglePlay(), {
        signal,
      });
      muteButton?.addEventListener("click", () => this.toggleMute(), {
        signal,
      });

      if (!seekTrack) return;

      const seekFromEvent = (event) => {
        const rect = seekTrack.getBoundingClientRect();
        if (!rect.width) return;
        const clientX =
          event.touches?.[0]?.clientX ??
          event.changedTouches?.[0]?.clientX ??
          event.clientX;
        if (!Number.isFinite(clientX)) return;
        const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
        this.seekToRatio(ratio);
      };

      seekTrack.addEventListener("pointerdown", (event) => {
        event.preventDefault();
        seekFromEvent(event);
      }, { signal });

      seekTrack.addEventListener(
        "keydown",
        (event) => {
          const video = this.getActiveVideo();
          if (!video || !video.duration) return;

          if (event.key === "ArrowLeft") {
            event.preventDefault();
            video.currentTime = Math.max(0, video.currentTime - 5);
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            video.currentTime = Math.min(
              video.duration,
              video.currentTime + 5,
            );
          }
        },
        { signal },
      );
    });
  }

  formatTime(seconds) {
    return formatAudioDurationShort(seconds);
  }

  updateControlUi(video) {
    const current = video?.currentTime || 0;
    const duration = video?.duration || 0;
    const playing = Boolean(video && !video.paused && !video.ended);
    const muted = video ? video.muted : this.userMuted;
    const progress = duration ? current / duration : 0;

    window.ProductAudio?.tickVideoWaves?.(this.controlRoots, video);

    this.controlRoots.forEach((root) => {
      const currentEl = root.querySelector("[data-full-teaser-time-current]");
      const totalEl = root.querySelector("[data-full-teaser-time-total]");
      const scrubber = root.querySelector("[data-audio-scrubber]");
      const seekTrack = root.querySelector("[data-audio-wave]");
      const playButton = root.querySelector("[data-audio-play]");
      const muteButton = root.querySelector("[data-audio-mute]");
      const hasWave = Boolean(root.videoWave?.url);

      if (currentEl) {
        const remaining = duration ? Math.max(0, duration - current) : 0;
        currentEl.textContent = `-${this.formatTime(remaining)}`;
      }
      if (totalEl) totalEl.textContent = this.formatTime(duration);

      // wave helper owns scrubber position once peaks are ready
      if (scrubber && !hasWave) scrubber.style.left = `${progress * 100}%`;

      if (seekTrack) {
        seekTrack.setAttribute(
          "aria-valuenow",
          String(Math.round(progress * 100)),
        );
      }

      root.classList.toggle("is-playing", playing);
      root.classList.toggle("is-muted", muted);

      if (playButton) {
        playButton.setAttribute(
          "aria-label",
          playing ? "Pause video" : "Play video",
        );
      }
      if (muteButton) {
        muteButton.setAttribute(
          "aria-label",
          muted ? "Unmute video" : "Mute video",
        );
      }
    });
  }

  unbindActiveVideo() {
    if (!this.boundVideo || !this.onBoundVideoEvent) return;

    [
      "timeupdate",
      "loadedmetadata",
      "durationchange",
      "play",
      "pause",
      "ended",
      "volumechange",
    ].forEach((eventName) => {
      this.boundVideo.removeEventListener(eventName, this.onBoundVideoEvent);
    });

    this.boundVideo = null;
  }

  bindActiveVideo() {
    const video = this.getActiveVideo();
    if (video === this.boundVideo) {
      this.updateControlUi(video);
      return;
    }

    this.unbindActiveVideo();
    this.boundVideo = video;

    if (!video) {
      window.ProductAudio?.syncVideoWaves?.(this.controlRoots, null);
      this.updateControlUi(null);
      return;
    }

    this.onBoundVideoEvent = () => this.updateControlUi(video);
    [
      "timeupdate",
      "loadedmetadata",
      "durationchange",
      "play",
      "pause",
      "ended",
      "volumechange",
    ].forEach((eventName) => {
      video.addEventListener(eventName, this.onBoundVideoEvent);
    });

    window.ProductAudio?.syncVideoWaves?.(this.controlRoots, video);
    this.updateControlUi(video);
  }

  togglePlay() {
    const video = this.getActiveVideo();
    if (!video) return;

    if (video.paused || video.ended) {
      this.userPaused = false;
      video.muted = this.userMuted;
      this.playVideo(video);
      return;
    }

    this.userPaused = true;
    video.pause();
  }

  toggleMute() {
    this.userMuted = !this.userMuted;

    const video = this.getActiveVideo();
    if (video) {
      video.muted = this.userMuted;
      // unmute often requires a play gesture — keep playback going
      if (!this.userMuted && video.paused && !this.userPaused) {
        this.playVideo(video);
      }
    }

    this.updateControlUi(video);
  }

  seekToRatio(ratio) {
    const video = this.getActiveVideo();
    if (!video || !video.duration) return;
    video.currentTime = ratio * video.duration;
    this.updateControlUi(video);
  }

  pauseAllVideos() {
    this.section.querySelectorAll("video").forEach((video) => {
      video.pause();
    });
  }

  playVideo(video, reset = false) {
    if (!video) return;
    if (reset) video.currentTime = 0;
    video.play().catch(() => {});
  }

  setHover(index) {
    if (!this.isDesktop()) return;
    this.hoverIndex = index;
    this.syncVideos();
  }

  syncMobileAudioLayout() {
    if (!this.mobileAudio) return;

    if (this.isDesktop() || !this.headerMenu) {
      this.mobileAudio.style.width = "";
      this.mobileAudio.style.bottom = "";
      this.mobileAudio.style.top = "";
      this.mobileAudio.style.marginTop = "";
      this.mobileAudio.style.maxHeight = "";
      return;
    }

    syncHeaderMenuDialogLayout(this.mobileAudio, this.headerMenu);
    this.mobileAudio.style.maxHeight = "";
  }

  syncMobileSnapScroll(enabled) {
    document.documentElement.classList.toggle(
      "full-teasers-snap",
      Boolean(enabled) && !this.isDesktop(),
    );
  }

  teardownMobileSnapScroll() {
    if (this.mobileSnapRaf) {
      cancelAnimationFrame(this.mobileSnapRaf);
      this.mobileSnapRaf = null;
    }
    if (this.mobileSnapScrollHandler) {
      window.removeEventListener("scroll", this.mobileSnapScrollHandler);
      window.removeEventListener("resize", this.mobileSnapScrollHandler);
      this.mobileSnapScrollHandler = null;
    }
    if (this.mobileSnapTouchMoveHandler) {
      window.removeEventListener("touchmove", this.mobileSnapTouchMoveHandler);
      this.mobileSnapTouchMoveHandler = null;
    }
    if (this.mobileSnapTouchStartHandler) {
      window.removeEventListener("touchstart", this.mobileSnapTouchStartHandler);
      this.mobileSnapTouchStartHandler = null;
    }
    this.mobileSnapTouchY = null;
    this.mobileSnapReleased = false;
    this.mobileSnapLastSettled = false;
  }

  // snap through every teaser; release only after the last one has settled
  updateMobileSnapScroll() {
    if (this.isDesktop() || this.mobileItems.length === 0) {
      this.mobileSnapReleased = false;
      this.mobileSnapLastSettled = false;
      this.syncMobileSnapScroll(false);
      return;
    }

    const first = this.mobileItems[0]?.getBoundingClientRect();
    const last =
      this.mobileItems[this.mobileItems.length - 1]?.getBoundingClientRect();
    if (!first || !last) {
      this.syncMobileSnapScroll(false);
      return;
    }

    const center = window.innerHeight * 0.5;
    const lastMid = last.top + last.height / 2;
    const distanceFromCenter = Math.abs(lastMid - center);

    // scrolled back up — re-enable snap through the list
    if (lastMid > center + 48) {
      this.mobileSnapReleased = false;
      this.mobileSnapLastSettled = false;
    }

    // above the section
    if (first.top > center + 24) {
      this.syncMobileSnapScroll(false);
      return;
    }

    // mark last teaser as snapped once it lands near center
    if (distanceFromCenter <= 36) {
      this.mobileSnapLastSettled = true;
    }

    // after last has snapped, further scroll toward footer turns snap off
    if (this.mobileSnapReleased || (this.mobileSnapLastSettled && lastMid < center - 12)) {
      this.mobileSnapReleased = true;
      this.syncMobileSnapScroll(false);
      return;
    }

    this.syncMobileSnapScroll(true);
  }

  setupMobileSnapScroll() {
    this.teardownMobileSnapScroll();
    this.syncMobileSnapScroll(false);

    if (this.isDesktop() || this.mobileItems.length === 0) return;

    this.mobileSnapScrollHandler = () => {
      if (this.mobileSnapRaf) return;
      this.mobileSnapRaf = requestAnimationFrame(() => {
        this.mobileSnapRaf = null;
        this.updateMobileSnapScroll();
      });
    };

    this.mobileSnapTouchStartHandler = (event) => {
      this.mobileSnapTouchY = event.touches?.[0]?.clientY ?? null;
    };

    // once last is settled, release mid-swipe so snap can't pull back
    this.mobileSnapTouchMoveHandler = (event) => {
      if (this.isDesktop() || this.mobileSnapTouchY == null) return;
      if (!this.mobileSnapLastSettled || this.mobileSnapReleased) return;

      const y = event.touches?.[0]?.clientY;
      if (!Number.isFinite(y)) return;

      const deltaY = this.mobileSnapTouchY - y;
      this.mobileSnapTouchY = y;

      // finger moving up = page scrolling down toward footer
      if (deltaY < 4) return;

      this.mobileSnapReleased = true;
      this.syncMobileSnapScroll(false);
    };

    window.addEventListener("scroll", this.mobileSnapScrollHandler, {
      passive: true,
    });
    window.addEventListener("resize", this.mobileSnapScrollHandler, {
      passive: true,
    });
    window.addEventListener("touchstart", this.mobileSnapTouchStartHandler, {
      passive: true,
    });
    window.addEventListener("touchmove", this.mobileSnapTouchMoveHandler, {
      passive: true,
    });
    this.updateMobileSnapScroll();
  }

  setupMobileObserver() {
    this.mobileObserver?.disconnect();
    this.mobileObserver = null;
    this.mobileVisibility = new Map();

    if (this.isDesktop() || this.mobileItems.length === 0) {
      this.syncMobileSnapScroll(false);
      return;
    }

    this.mobileObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.dataset.fullTeaserMobileItem);
          if (Number.isNaN(index)) return;

          this.mobileVisibility.set(index, {
            ratio: entry.intersectionRatio,
            isIntersecting: entry.isIntersecting,
            top: entry.boundingClientRect.top,
          });
        });

        const activateLine = window.innerHeight * 0.5;
        let bestIndex = null;
        let bestScore = -Infinity;

        this.mobileVisibility.forEach((state, index) => {
          if (!state.isIntersecting || state.ratio < 0.35) return;

          const distance = Math.abs(state.top - activateLine);
          const score = state.ratio * 800 - distance;
          if (score > bestScore) {
            bestScore = score;
            bestIndex = index;
          }
        });

        if (bestIndex === null || bestIndex === this.activeIndex) return;

        const current = this.mobileVisibility.get(this.activeIndex);
        if (current?.isIntersecting && current.ratio >= 0.42) {
          return;
        }

        this.setActive(bestIndex, true);
      },
      {
        root: null,
        threshold: [0, 0.25, 0.35, 0.45, 0.55, 0.7, 1],
        rootMargin: "-30% 0px -30% 0px",
      },
    );

    this.mobileItems.forEach((item) => this.mobileObserver.observe(item));
  }

  // play hovered thumb / active panel / active mobile slide; pause the rest
  syncVideos(resetActivePanel = false) {
    this.items.forEach((item, index) => {
      const video = item.querySelector("video");
      if (!video) return;

      // thumb previews stay muted
      video.muted = true;

      if (this.isDesktop() && index === this.hoverIndex) {
        this.playVideo(video);
      } else {
        video.pause();
      }
    });

    this.panels.forEach((panel, index) => {
      const video = panel.querySelector("video");
      if (!video) return;

      const isActive = this.isDesktop() && index === this.activeIndex;
      video.muted = isActive ? this.userMuted : true;

      if (isActive && !this.userPaused) {
        this.playVideo(video, resetActivePanel);
      } else {
        video.pause();
      }
    });

    this.mobileItems.forEach((item, index) => {
      const video = item.querySelector("video");
      if (!video) return;

      const isActive = !this.isDesktop() && index === this.activeIndex;
      video.muted = isActive ? this.userMuted : true;

      if (isActive && !this.userPaused) {
        this.playVideo(video, resetActivePanel);
      } else {
        video.pause();
      }
    });
  }

  setActive(index, reset = true) {
    if (index < 0 || index >= this.slideCount) return;

    this.activeIndex = index;
    this.section.setAttribute("data-active", String(index));
    this.userPaused = false;

    this.syncVideos(reset);
    this.bindActiveVideo();
    if (!this.isDesktop()) this.updateMobileSnapScroll();
  }
}

const initFullTeasers = (scope = document) => {
  whenVideoWavesReady(() => {
    scope.querySelectorAll("[data-full-teasers]").forEach((section) => {
      if (section.fullTeasers) {
        section.fullTeasers.destroy();
      }

      section.fullTeasers = new FullTeasers(section);
    });
  });
};

class TeaserGrid {
  constructor(grid) {
    this.grid = grid;
    this.abortController = new AbortController();
    this.items = [...grid.querySelectorAll("[data-teaser-grid-item]")];
    this.panels = [...grid.querySelectorAll("[data-teaser-grid-panel]")];
    this.mediaQuery = window.matchMedia("(min-width: 768px)");
    this.activeIndex = 0;

    this.thumbVideos = this.items
      .map((item, index) => ({
        index,
        video: item.querySelector("video"),
      }))
      .filter(({ video }) => video);

    this.panelVideos = this.panels
      .map((panel, index) => ({
        index,
        video: panel.querySelector("video"),
      }))
      .filter(({ video }) => video);

    const { signal } = this.abortController;

    if (this.items.length === 0) return;

    this.items.forEach((item, index) => {
      item.addEventListener(
        "click",
        (event) => {
          if (this.isDesktop()) return;

          event.preventDefault();
          this.setActive(index);
        },
        { signal },
      );
      item.addEventListener(
        "mouseenter",
        () => {
          if (!this.isDesktop()) return;
          this.setActive(index);
        },
        { signal },
      );
      item.addEventListener(
        "focusin",
        () => {
          if (!this.isDesktop()) return;
          this.setActive(index);
        },
        { signal },
      );
    });

    [...this.thumbVideos, ...this.panelVideos].forEach(({ video }) => {
      video.loop = false;
      video.addEventListener("ended", () => this.advance(), { signal });
    });

    const onViewportChange = () => this.syncVideos(true);
    if (typeof this.mediaQuery.addEventListener === "function") {
      this.mediaQuery.addEventListener("change", onViewportChange, { signal });
    } else {
      this.mediaQuery.addListener(onViewportChange);
      signal.addEventListener("abort", () => {
        this.mediaQuery.removeListener(onViewportChange);
      });
    }

    this.setActive(
      this.thumbVideos[0]?.index ?? this.panelVideos[0]?.index ?? 0,
    );
  }

  isDesktop() {
    return this.mediaQuery.matches;
  }

  destroy() {
    this.abortController.abort();
    this.pauseAll();
    delete this.grid.teaserGrid;
  }

  pauseAll() {
    [...this.thumbVideos, ...this.panelVideos].forEach(({ video }) => {
      video.pause();
    });
  }

  playVideo(video, reset = false) {
    if (!video) return;
    if (reset) video.currentTime = 0;
    video.play().catch(() => {});
  }

  syncVideos(reset = false) {
    const desktop = this.isDesktop();

    this.thumbVideos.forEach(({ index, video }) => {
      if (desktop && index === this.activeIndex) {
        this.playVideo(video, reset);
      } else {
        video.pause();
      }
    });

    this.panelVideos.forEach(({ index, video }) => {
      if (!desktop && index === this.activeIndex) {
        this.playVideo(video, reset);
      } else {
        video.pause();
      }
    });
  }

  setActive(index) {
    if (index < 0 || index >= this.items.length) return;

    this.activeIndex = index;
    this.grid.setAttribute("data-active", String(index));
    this.syncVideos(true);
  }

  advance() {
    const playlist = this.isDesktop() ? this.thumbVideos : this.panelVideos;
    if (playlist.length === 0) return;

    const currentSeqIndex = playlist.findIndex(
      ({ index }) => index === this.activeIndex,
    );
    const nextSeqIndex = (currentSeqIndex + 1) % playlist.length;
    this.setActive(playlist[nextSeqIndex].index);
  }
}

const initTeaserGrids = (scope = document) => {
  scope.querySelectorAll("[data-teaser-grid]").forEach((grid) => {
    if (grid.teaserGrid) {
      grid.teaserGrid.destroy();
    }

    grid.teaserGrid = new TeaserGrid(grid);
  });
};

// products slider: hover thumb to set active product
class ProductsSlider {
  constructor(section) {
    this.section = section;
    this.abortController = new AbortController();
    this.thumbs = [...section.querySelectorAll("[data-products-slider-thumb]")];
    this.panels = [...section.querySelectorAll("[data-products-slider-panel]")];

    if (this.thumbs.length === 0) return;

    const { signal } = this.abortController;
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener("mouseenter", () => this.setActive(index), {
        signal,
      });
      thumb.addEventListener("focus", () => this.setActive(index), { signal });
    });

    this.setActive(0);
  }

  destroy() {
    this.abortController.abort();
    delete this.section.productsSlider;
  }

  setActive(index) {
    this.section.setAttribute("data-active", String(index));

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      setCollapsiblePanelHidden(panel, !isActive);
      panel.tabIndex = isActive ? 0 : -1;
    });
  }
}

const initProductsSliders = (scope = document) => {
  scope.querySelectorAll("[data-products-slider]").forEach((section) => {
    if (section.productsSlider) {
      section.productsSlider.destroy();
    }

    section.productsSlider = new ProductsSlider(section);
  });
};

class Locations {
  constructor(section) {
    this.section = section;
    this.abortController = new AbortController();
    this.items = [...section.querySelectorAll("[data-location-item]")];

    if (this.items.length <= 1) return;

    const { signal } = this.abortController;
    this.activeIndex = 0;
    this.intervalId = null;

    section.addEventListener("mouseenter", () => this.stopInterval(), {
      signal,
    });
    section.addEventListener("mouseleave", () => this.startInterval(), {
      signal,
    });

    this.setActive(0);
    this.startInterval();
  }

  destroy() {
    this.abortController.abort();
    this.stopInterval();
    delete this.section.locations;
  }

  startInterval() {
    this.stopInterval();
    this.intervalId = setInterval(() => this.advance(), 3000);
  }

  stopInterval() {
    if (!this.intervalId) return;

    clearInterval(this.intervalId);
    this.intervalId = null;
  }

  setActive(index) {
    this.activeIndex = index;
    this.section.setAttribute("data-active", String(index));
  }

  advance() {
    const nextIndex = (this.activeIndex + 1) % this.items.length;
    this.setActive(nextIndex);
  }
}

const initLocations = (scope = document) => {
  scope.querySelectorAll("[data-locations]").forEach((section) => {
    if (section.locations) {
      section.locations.destroy();
    }

    section.locations = new Locations(section);
  });
};

const destroySectionModules = (scope) => {
  scope.querySelectorAll("[data-full-teasers]").forEach((section) => {
    section.fullTeasers?.destroy?.();
  });

  scope.querySelectorAll("[data-teaser-grid]").forEach((grid) => {
    grid.teaserGrid?.destroy?.();
  });

  scope.querySelectorAll("[data-sounds-list]").forEach((section) => {
    section.soundsList?.destroy?.();
  });

  scope.querySelectorAll("[data-collections-list]").forEach((section) => {
    section.collectionsList?.destroy?.();
  });

  scope.querySelectorAll("[data-products-slider]").forEach((section) => {
    section.productsSlider?.destroy?.();
  });

  scope.querySelectorAll("[data-locations]").forEach((section) => {
    section.locations?.destroy?.();
  });
};

runWhenReady(() => {
  initProductsLists();
  initCollectionsLists();
  initSoundsLists();
  initFullTeasers();
  initTeaserGrids();
  initProductsSliders();
  initLocations();
});

document.addEventListener("shopify:section:load", (event) => {
  initProductsLists(event.target);
  initCollectionsLists(event.target);
  initSoundsLists(event.target);
  initFullTeasers(event.target);
  initTeaserGrids(event.target);
  initProductsSliders(event.target);
  initLocations(event.target);
  initTextMediaDetails(event.target);
});

document.addEventListener("shopify:section:unload", (event) => {
  destroySectionModules(event.target);
});

document.addEventListener("shopify:section:reorder", (event) => {
  initProductsLists(event.target);
  initCollectionsLists(event.target);
  initSoundsLists(event.target);
  initFullTeasers(event.target);
  initTeaserGrids(event.target);
  initProductsSliders(event.target);
  initLocations(event.target);
  initTextMediaDetails(event.target);
});

(function () {
  let interacted = false;
  let allowDismissPassThrough = false;
  const FADE_MS = 300;
  const DISMISS_BUTTON_SELECTOR = [
    "#shopify-pc__banner__btn-accept",
    "#shopify-pc__banner__btn-decline",
    "#shopify-pc__prefs__header-accept",
    "#shopify-pc__prefs__header-decline",
    "#shopify-pc__prefs__header-save",
  ].join(", ");

  function fadeIn(el) {
    if (!el || el.classList.contains("is-visible")) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.classList.add("is-visible");
      });
    });
  }

  function syncBannerToMenu(dialog) {
    const menu = document.querySelector("[data-header-menu]");
    if (!dialog || !menu) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) {
      dialog.style.removeProperty("left");
      dialog.style.removeProperty("right");
      dialog.style.removeProperty("width");
      dialog.style.removeProperty("bottom");
      return;
    }

    // match filter / currency gap above the menu
    const rect = menu.getBoundingClientRect();
    const gap = 10;
    dialog.style.setProperty("left", `${rect.left}px`, "important");
    dialog.style.setProperty("right", "auto", "important");
    dialog.style.setProperty("width", `${rect.width}px`, "important");
    dialog.style.setProperty(
      "bottom",
      `${Math.max(gap, window.innerHeight - rect.top + gap)}px`,
      "important",
    );
  }

  function initBannerScroll(dialog) {
    dialog.addEventListener("click", () => {
      interacted = true;
      dialog.classList.remove("scroll-bg");
    });

    window.addEventListener(
      "scroll",
      () => {
        if (!interacted) dialog.classList.add("scroll-bg");
      },
      { passive: true },
    );

    syncBannerToMenu(dialog);
    fadeIn(dialog);

    const menu = document.querySelector("[data-header-menu]");
    const onLayout = () => syncBannerToMenu(dialog);
    window.addEventListener("resize", onLayout, { passive: true });

    if (menu && typeof ResizeObserver !== "undefined") {
      const menuObserver = new ResizeObserver(onLayout);
      menuObserver.observe(menu);
    }
  }

  document.addEventListener(
    "click",
    (event) => {
      if (allowDismissPassThrough) return;

      const button = event.target.closest(DISMISS_BUTTON_SELECTOR);
      if (!(button instanceof HTMLElement)) return;

      const banner = button.closest(".shopify-pc__banner__dialog");
      const prefs = button.closest(
        "#shopify-pc__prefs__dialog, .shopify-pc__prefs__dialog",
      );
      const panel = banner || prefs;
      if (!panel || panel.classList.contains("is-fading")) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      panel.classList.remove("is-visible");
      panel.classList.add("is-fading");

      window.setTimeout(() => {
        allowDismissPassThrough = true;
        button.click();
        allowDismissPassThrough = false;
      }, FADE_MS);
    },
    true,
  );

  // fade in prefs when manage preferences opens
  const prefsObserver = new MutationObserver(() => {
    const prefs = document.querySelector(
      "#shopify-pc__prefs__dialog, .shopify-pc__prefs__dialog",
    );
    if (prefs && !prefs.classList.contains("is-fading")) {
      fadeIn(prefs);
    }
  });
  prefsObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class", "hidden", "aria-hidden"],
  });

  const dialog = document.querySelector(".shopify-pc__banner__dialog");
  if (dialog) {
    initBannerScroll(dialog);
    return;
  }

  const observer = new MutationObserver((_, obs) => {
    const el = document.querySelector(".shopify-pc__banner__dialog");
    if (el) {
      obs.disconnect();
      initBannerScroll(el);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();

// bespoke appointment datepicker and time select
(() => {
  const datepicker = document.querySelector("[data-bespoke-datepicker]");
  const timepicker = document.querySelector("[data-bespoke-timepicker]");

  const setPanelOpen = (root, toggle, panel, arrow, isOpen) => {
    if (!root || !toggle || !panel) return;
    toggle.setAttribute("aria-expanded", String(isOpen));
    setCollapsiblePanelHidden(panel, !isOpen);
    arrow?.classList.toggle("rotate-180", isOpen);

    if (isOpen) {
      root.setAttribute("data-open", "");
      return;
    }

    root.removeAttribute("data-open");
  };

  if (timepicker) {
    const toggle = timepicker.querySelector("[data-bespoke-timepicker-toggle]");
    const arrow = timepicker.querySelector("[data-bespoke-timepicker-arrow]");
    const label = timepicker.querySelector("[data-bespoke-timepicker-label]");
    const input = timepicker.querySelector("[data-bespoke-timepicker-input]");
    const panel = timepicker.querySelector("[data-bespoke-timepicker-panel]");
    const options = timepicker.querySelectorAll(
      "[data-bespoke-timepicker-option]",
    );
    const placeholder = input?.dataset.placeholder || "Time*";

    if (toggle && input && panel && label) {
      const syncTriggerLabel = () => {
        if (input.value) {
          label.textContent = input.value;
          toggle.classList.remove("text-black-light");
          toggle.classList.add("text-black");
          return;
        }

        label.textContent = placeholder;
        toggle.classList.add("text-black-light");
        toggle.classList.remove("text-black");
      };

      const setOpen = (isOpen) => {
        setPanelOpen(timepicker, toggle, panel, arrow, isOpen);
      };

      const selectTime = (value) => {
        input.value = value;
        input.dispatchEvent(new Event("input", { bubbles: true }));
        options.forEach((option) => {
          const isSelected = option.dataset.value === value;
          option.classList.toggle("underline", isSelected);
          option.classList.toggle("underline-offset-4", isSelected);
          option.setAttribute("aria-selected", String(isSelected));
        });
        syncTriggerLabel();
        setOpen(false);
      };

      toggle.addEventListener("click", () => {
        const willOpen = toggle.getAttribute("aria-expanded") !== "true";
        if (willOpen && datepicker) {
          const dateToggle = datepicker.querySelector(
            "[data-bespoke-datepicker-toggle]",
          );
          const datePanel = datepicker.querySelector(
            "[data-bespoke-datepicker-panel]",
          );
          const dateArrow = datepicker.querySelector(
            "[data-bespoke-datepicker-arrow]",
          );
          setPanelOpen(datepicker, dateToggle, datePanel, dateArrow, false);
        }
        setOpen(willOpen);
      });

      options.forEach((option) => {
        option.addEventListener("click", () => {
          selectTime(option.dataset.value || "");
        });
      });

      input.addEventListener("invalid", () => {
        setOpen(true);
        toggle.focus();
      });

      syncTriggerLabel();
    }
  }

  if (!datepicker) return;

  const toggle = datepicker.querySelector("[data-bespoke-datepicker-toggle]");
  const arrow = datepicker.querySelector("[data-bespoke-datepicker-arrow]");
  const label = datepicker.querySelector("[data-bespoke-datepicker-label]");
  const input = datepicker.querySelector("[data-bespoke-datepicker-input]");
  const panel = datepicker.querySelector("[data-bespoke-datepicker-panel]");
  const monthLabel = datepicker.querySelector(
    "[data-bespoke-datepicker-month]",
  );
  const daysGrid = datepicker.querySelector("[data-bespoke-datepicker-days]");
  const prevButton = datepicker.querySelector("[data-bespoke-datepicker-prev]");
  const nextButton = datepicker.querySelector("[data-bespoke-datepicker-next]");
  const placeholder = input?.dataset.placeholder || "Date*";

  if (
    !toggle ||
    !input ||
    !panel ||
    !monthLabel ||
    !daysGrid ||
    !prevButton ||
    !nextButton
  ) {
    return;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 0=sun … 6=sat; default mon–sat (no sunday)
  const parseAvailableDays = (value) =>
    new Set(
      (value || "1,2,3,4,5,6")
        .split(",")
        .map((day) => day.trim())
        .filter(Boolean)
        .map(Number)
        .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6),
    );
  const availableDays = parseAvailableDays(datepicker.dataset.availableDays);
  const isAvailableDay = (date) => availableDays.has(date.getDay());

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  let viewYear = today.getFullYear();
  let viewMonth = today.getMonth();
  let selectedDate = null;

  if (input.value) {
    const parsed = parseDateValue(input.value);
    if (parsed && isAvailableDay(parsed)) {
      selectedDate = parsed;
      viewYear = parsed.getFullYear();
      viewMonth = parsed.getMonth();
      syncTriggerLabel();
    } else if (parsed) {
      input.value = "";
    }
  }

  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  function parseDateValue(value) {
    const slash = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (slash) {
      const date = new Date(
        Number(slash[3]),
        Number(slash[1]) - 1,
        Number(slash[2]),
      );
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!iso) return null;
    const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function formatDateValue(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  function syncTriggerLabel() {
    if (selectedDate) {
      label.textContent = formatDateValue(selectedDate);
      toggle.classList.remove("text-black-light");
      toggle.classList.add("text-black");
      return;
    }

    label.textContent = placeholder;
    toggle.classList.add("text-black-light");
    toggle.classList.remove("text-black");
  }

  function canGoPrev() {
    return (
      viewYear > today.getFullYear() ||
      (viewYear === today.getFullYear() && viewMonth > today.getMonth())
    );
  }

  function setOpen(isOpen) {
    if (isOpen && timepicker) {
      const timeToggle = timepicker.querySelector(
        "[data-bespoke-timepicker-toggle]",
      );
      const timePanel = timepicker.querySelector(
        "[data-bespoke-timepicker-panel]",
      );
      const timeArrow = timepicker.querySelector(
        "[data-bespoke-timepicker-arrow]",
      );
      setPanelOpen(timepicker, timeToggle, timePanel, timeArrow, false);
    }
    setPanelOpen(datepicker, toggle, panel, arrow, isOpen);
  }

  function renderCalendar() {
    monthLabel.textContent = `${monthNames[viewMonth]} ${viewYear}`;
    prevButton.disabled = !canGoPrev();
    daysGrid.replaceChildren();

    const firstDay = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const startOffset = firstDay.getDay();

    for (let i = 0; i < startOffset; i += 1) {
      const spacer = document.createElement("span");
      spacer.className = "size-8";
      spacer.setAttribute("aria-hidden", "true");
      daysGrid.appendChild(spacer);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(viewYear, viewMonth, day);
      const isDisabled = date < today || !isAvailableDay(date);
      const isSelected = isSameDay(date, selectedDate);
      const button = document.createElement("button");

      button.type = "button";
      button.textContent = String(day);
      button.disabled = isDisabled;
      button.className = [
        "flex size-8 items-center justify-center rounded-full border",
        isDisabled
          ? "border-transparent text-black-light cursor-not-allowed"
          : isSelected
            ? "border-black text-black cursor-pointer"
            : "border-transparent text-black cursor-pointer hover:border-black",
      ].join(" ");

      if (isSelected) {
        button.setAttribute("aria-current", "date");
      }

      if (!isDisabled) {
        button.addEventListener("click", () => {
          selectedDate = date;
          input.value = formatDateValue(date);
          input.dispatchEvent(new Event("input", { bubbles: true }));
          syncTriggerLabel();
          renderCalendar();
        });
      }

      daysGrid.appendChild(button);
    }
  }

  toggle.addEventListener("click", () => {
    const willOpen = toggle.getAttribute("aria-expanded") !== "true";
    setOpen(willOpen);
    if (willOpen) renderCalendar();
  });

  prevButton.addEventListener("click", () => {
    if (!canGoPrev()) return;
    viewMonth -= 1;
    if (viewMonth < 0) {
      viewMonth = 11;
      viewYear -= 1;
    }
    renderCalendar();
  });

  nextButton.addEventListener("click", () => {
    viewMonth += 1;
    if (viewMonth > 11) {
      viewMonth = 0;
      viewYear += 1;
    }
    renderCalendar();
  });

  input.addEventListener("invalid", () => {
    setOpen(true);
    renderCalendar();
    toggle.focus();
  });

  renderCalendar();
})();

// inline form field validation for bespoke and contact
(() => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const isValidName = (value) => value.length >= 2 && /[a-z]/i.test(value);

  const isValidPhone = (value) => {
    const digits = value.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15;
  };

  const isValidDate = (value, form) => {
    const slash = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (!slash) return false;

    const date = new Date(
      Number(slash[3]),
      Number(slash[1]) - 1,
      Number(slash[2]),
    );
    if (Number.isNaN(date.getTime())) return false;
    if (date.getMonth() !== Number(slash[1]) - 1) return false;
    if (date.getDate() !== Number(slash[2])) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return false;

    const datepicker = form?.querySelector("[data-bespoke-datepicker]");
    const availableDays = new Set(
      (datepicker?.dataset.availableDays || "1,2,3,4,5,6")
        .split(",")
        .map((day) => day.trim())
        .filter(Boolean)
        .map(Number)
        .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6),
    );
    return availableDays.has(date.getDay());
  };

  const initInlineFormValidation = (form) => {
    if (!form) return;

    form.setAttribute("novalidate", "");

    const fields = [
      ...form.querySelectorAll("[data-bespoke-field], [data-form-field]"),
    ];
    const getErrorEl = (field) =>
      field.querySelector("[data-bespoke-error], [data-form-error]");
    let submitted = fields.some((field) => {
      const errorEl = getErrorEl(field);
      return errorEl && !errorEl.classList.contains("hidden");
    });

    const submitButton = form.querySelector(
      "[data-bespoke-submit], [data-form-submit]",
    );
    const getInput = (field) => field.querySelector("input, textarea");
    const getFieldName = (field) =>
      field.dataset.bespokeField || field.dataset.formField;
    const getMandatory = (field) =>
      field.dataset.bespokeErrorMandatory || field.dataset.errorMandatory || "";
    const getInvalid = (field) =>
      field.dataset.bespokeErrorInvalid || field.dataset.errorInvalid || "";

    const isValidTime = (value) => {
      const options = form.querySelectorAll("[data-bespoke-timepicker-option]");
      return [...options].some((option) => option.dataset.value === value);
    };

    const validators = {
      name: isValidName,
      email: (value) => emailPattern.test(value),
      phone: isValidPhone,
      date: (value) => isValidDate(value, form),
      time: isValidTime,
      comment: (value) => value.length >= 2,
    };

    const getErrorMessage = (field) => {
      const input = getInput(field);
      const value = (input?.value || "").trim();
      const mandatory = getMandatory(field);
      const invalid = getInvalid(field);
      const isValid = validators[getFieldName(field)];

      if (!value) return mandatory;
      if (isValid && !isValid(value)) return invalid;
      return "";
    };

    const setFieldError = (field, message) => {
      const errorEl = getErrorEl(field);
      const input = getInput(field);
      if (!errorEl) return;

      errorEl.textContent = message;
      errorEl.classList.toggle("hidden", !message);
      input?.setAttribute("aria-invalid", message ? "true" : "false");
    };

    const validateField = (field) => {
      setFieldError(field, getErrorMessage(field));
    };

    const syncSubmitState = () => {
      if (!submitButton) return;
      const complete = fields.every((field) => !getErrorMessage(field));
      submitButton.classList.toggle("underline", complete);
      submitButton.classList.toggle("underline-offset-2", complete);
      submitButton.classList.toggle("text-black", complete);
      submitButton.classList.toggle("text-black-light", !complete);
    };

    const focusField = (field) => {
      const control =
        field.querySelector("[data-bespoke-datepicker-toggle]") ||
        field.querySelector("[data-bespoke-timepicker-toggle]") ||
        field.querySelector("input:not(.sr-only), textarea");
      control?.focus();
    };

    const validateForm = () => {
      submitted = true;
      let firstInvalid = null;

      fields.forEach((field) => {
        const message = getErrorMessage(field);
        setFieldError(field, message);
        if (message && !firstInvalid) firstInvalid = field;
      });

      if (firstInvalid) focusField(firstInvalid);
      return !firstInvalid;
    };

    form.addEventListener("submit", (event) => {
      if (!validateForm()) event.preventDefault();
    });

    fields.forEach((field) => {
      const input = getInput(field);
      if (!input) return;

      const onUpdate = () => {
        if (submitted) validateField(field);
        syncSubmitState();
      };

      input.addEventListener("input", onUpdate);
      input.addEventListener("change", onUpdate);
    });

    syncSubmitState();
  };

  initInlineFormValidation(document.querySelector("#bespoke-form"));
  initInlineFormValidation(document.querySelector("#contact-form"));
})();

// grow vision field as text is entered
(() => {
  const textarea = document.querySelector("[data-bespoke-autosize]");
  if (!textarea) return;

  const resize = () => {
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  textarea.addEventListener("input", resize);
  resize();
})();
